import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Database,
  RefreshCw,
  Shield,
  AlertTriangle,
  CheckCircle,
  HardDrive,
  Clock,
  Play,
  Eye,
  Download,
  Loader2,
  Info,
  Package,
  Truck,
  Users,
  FileText,
  RotateCcw,
  Trash2,
  FileDown,
  Upload,
  FileUp,
  Archive
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Source Aliases CRUD card — admin-editable merge rules for the Raw Funds
// Source Summary. DB-backed aliases overlay the hardcoded defaults so admins
// can add new bank aliases (e.g. "ICICI BANK" → "ESR ICICI") without a deploy.
// ---------------------------------------------------------------------------
const SourceAliasesCard = () => {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFrom, setNewFrom] = useState("");
  const [newTo, setNewTo] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchAliases = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/source-aliases`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) { toast.error("Failed to load aliases"); return; }
      const d = await r.json();
      setAliases(d.aliases || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAliases(); }, []);

  const addAlias = async () => {
    const fv = newFrom.trim().toUpperCase();
    const tv = newTo.trim().toUpperCase();
    if (!fv || !tv) { toast.error("Both From and To are required"); return; }
    if (fv === tv) { toast.error("From and To must differ"); return; }
    setSaving(true);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/source-aliases`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ from_value: fv, to_value: tv }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Save failed"); return; }
      toast.success(`Added ${fv} → ${tv}`);
      setNewFrom(""); setNewTo("");
      fetchAliases();
    } finally {
      setSaving(false);
    }
  };

  const deleteAlias = async (fv) => {
    if (!window.confirm(`Delete alias "${fv}"?`)) return;
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/source-aliases/${encodeURIComponent(fv)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${t}` },
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Delete failed"); return; }
      toast.success(`Deleted ${fv}`);
      fetchAliases();
    } catch (e) {
      toast.error("Network error: " + e.message);
    }
  };

  return (
    <Card data-testid="source-aliases-card" className="mt-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-indigo-600" />
            Source Aliases (Raw Funds bank/channel merges)
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={fetchAliases} disabled={loading} className="h-7 w-7 p-0" data-testid="source-aliases-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Merge rules used by the Raw Data Source Summary. Defaults are hardcoded; you can add custom rules below.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 mb-3 p-2 bg-indigo-50 rounded">
          <div className="flex-1">
            <label className="text-[11px] text-slate-600">From (raw value)</label>
            <Input value={newFrom} onChange={(e) => setNewFrom(e.target.value.toUpperCase())} placeholder="e.g. ICICI BANK" className="h-8 text-xs" data-testid="source-alias-from" />
          </div>
          <div className="flex-1">
            <label className="text-[11px] text-slate-600">To (canonical)</label>
            <Input value={newTo} onChange={(e) => setNewTo(e.target.value.toUpperCase())} placeholder="e.g. ESR ICICI" className="h-8 text-xs" data-testid="source-alias-to" />
          </div>
          <Button size="sm" onClick={addAlias} disabled={saving} data-testid="source-alias-add">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
          </Button>
        </div>
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-2">From (raw)</th>
                <th className="text-left p-2">→ To (canonical)</th>
                <th className="text-left p-2">Source</th>
                <th className="text-left p-2">Added By</th>
                <th className="text-center p-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {aliases.length === 0 && !loading ? (
                <tr><td colSpan={5} className="p-3 text-center text-slate-400 italic">No aliases</td></tr>
              ) : aliases.map((a) => (
                <tr key={a.from_value} className="border-t border-slate-100" data-testid={`source-alias-row-${a.from_value}`}>
                  <td className="p-2 font-mono">{a.from_value}</td>
                  <td className="p-2 font-mono font-semibold text-indigo-700">{a.to_value}</td>
                  <td className="p-2">
                    {a.source === "default" ? (
                      <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">Default</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">Custom</Badge>
                    )}
                  </td>
                  <td className="p-2 text-slate-500">{a.added_by || "—"}</td>
                  <td className="p-2 text-center">
                    {a.source === "db" && (
                      <Button size="sm" variant="ghost" onClick={() => deleteAlias(a.from_value)} className="h-6 w-6 p-0 text-rose-600 hover:bg-rose-50" data-testid={`source-alias-delete-${a.from_value}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Alias Audit card — surfaces ledger codes that ALSO exist as `client_aliases`
// entries, causing duplicate buckets in Client Balance / Summary. One-click
// cleanup re-points all references to the canonical and deletes the legacy
// ledger row.
// ---------------------------------------------------------------------------
const AliasAuditCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAlias, setBusyAlias] = useState(null);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/alias-audit`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Failed to load"); return; }
      setItems(d.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudit(); }, []);

  const cleanup = async (alias, canonical, totalRefs) => {
    if (!window.confirm(
      `Delete the legacy "${alias}" ledger and re-point its ${totalRefs.toLocaleString()} reference(s) to "${canonical}"?\n\nThis is irreversible.`
    )) return;
    setBusyAlias(alias);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/alias-audit/cleanup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ alias }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Cleanup failed"); return; }
      const moved = Object.values(d.refs_moved || {}).filter((v) => typeof v === "number").reduce((a, b) => a + b, 0);
      toast.success(`${alias} → ${canonical}: ${moved} refs re-pointed, legacy ledger removed`);
      fetchAudit();
    } finally {
      setBusyAlias(null);
    }
  };

  return (
    <Card data-testid="alias-audit-card" className="mt-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Alias Audit — Ledger codes that are also aliases ({items.length})
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={fetchAudit} disabled={loading} className="h-7 w-7 p-0" data-testid="alias-audit-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          When a code exists BOTH as a canonical ledger AND as an alias of another, Client Balance and Summary will show duplicate buckets. Clean up safely by re-pointing all references and removing the legacy ledger row.
        </p>
      </CardHeader>
      <CardContent>
        {items.length === 0 && !loading ? (
          <div className="text-center text-emerald-600 text-sm py-4 italic">
            All clean — no double-ledger aliases found.
          </div>
        ) : (
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-amber-50">
                <tr>
                  <th className="text-left p-2">Legacy Ledger (alias)</th>
                  <th className="text-left p-2">→ Canonical</th>
                  <th className="text-right p-2">Raw Funds</th>
                  <th className="text-right p-2">Shipments</th>
                  <th className="text-right p-2">Sales</th>
                  <th className="text-right p-2">Ledger Entries</th>
                  <th className="text-right p-2 font-semibold">Total Refs</th>
                  <th className="text-center p-2 w-32">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.alias} className="border-t border-slate-100" data-testid={`alias-audit-row-${it.alias}`}>
                    <td className="p-2 font-mono font-semibold text-rose-700">{it.alias}</td>
                    <td className="p-2 font-mono font-semibold text-emerald-700">{it.canonical}</td>
                    <td className="p-2 text-right">{(it.refs.raw_funds_rows || 0).toLocaleString()}</td>
                    <td className="p-2 text-right">{(it.refs.shipment_orders || 0).toLocaleString()}</td>
                    <td className="p-2 text-right">{(it.refs.sales || 0).toLocaleString()}</td>
                    <td className="p-2 text-right">{(it.refs.ledger_entries || 0).toLocaleString()}</td>
                    <td className="p-2 text-right font-bold text-slate-700">{it.total_refs.toLocaleString()}</td>
                    <td className="p-2 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                        onClick={() => cleanup(it.alias, it.canonical, it.total_refs)}
                        disabled={busyAlias === it.alias}
                        data-testid={`alias-audit-cleanup-${it.alias}`}
                      >
                        {busyAlias === it.alias ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Trash2 className="w-3 h-3 mr-1" />
                        )}
                        Clean up
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DataManagementPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  // Suspicious-dates scanner result (DataManagementPage scope)
  const [suspiciousResult, setSuspiciousResult] = useState(null);
  // Excel-strip date corruption tool state
  const [excelStripScan, setExcelStripScan] = useState(null);
  const [excelStripBusy, setExcelStripBusy] = useState(false);
  const [excelStripPassword, setExcelStripPassword] = useState("");
  
  // Backup state
  const [creatingBackup, setCreatingBackup] = useState(false);
  // Default-selected collections for export. Aligned with the backend's
  // restore ALLOWED set so a one-click backup → restore cycle is lossless.
  // payment_followups, aliases, price lists and advance_payments were
  // previously missing — that's why followups vanished after a restore.
  const [selectedCollections, setSelectedCollections] = useState([
    "shipment_orders", "sales", "products", "shipping", "ledgers",
    "partner_ledger_uploads", "ledger_entries", "notes", "activity_logs",
    "payment_followups",
    "client_aliases", "client_price_lists",
    "client_shipping_rates", "client_price_history",
    "source_aliases",
    "advance_payments",
  ]);
  
  // Restore state
  const [restoring, setRestoring] = useState(false);
  const [restoreConfirmId, setRestoreConfirmId] = useState(null);
  
  // Erase state
  const [erasing, setErasing] = useState(false);

  // Wipe 2025 state
  const [wipe2025Running, setWipe2025Running] = useState(false);
  const [wipe2025Counts, setWipe2025Counts] = useState(null);
  const [wipe2025Files, setWipe2025Files] = useState(null);
  const [wipe2025History, setWipe2025History] = useState([]);
  const [wipe2025RestoringId, setWipe2025RestoringId] = useState(null);
  const [wipe2025Years, setWipe2025Years] = useState("2025"); // comma-separated
  const [wipe2025Months, setWipe2025Months] = useState(""); // comma-separated (1-12), empty = all months

  // Recent activity feed
  const [activityFeed, setActivityFeed] = useState(null);
  const [activityHours, setActivityHours] = useState(24);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);

  // Backups (snapshot inventory)
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotsBusy, setSnapshotsBusy] = useState(null); // collection name being acted on
  const [snapshotsExpanded, setSnapshotsExpanded] = useState(false);
  
  // Download backup state
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [restoreProgress, setRestoreProgress] = useState(0);
  
  // Restore from external file state
  const [restoringExternal, setRestoringExternal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreStatus, setRestoreStatus] = useState(null); // {phase, current, total, inserted, ...}
  
  // Reprocess errors state
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);
  
  // Tracking-quality scanner state
  const [trackScanLoading, setTrackScanLoading] = useState(false);
  const [trackScanResult, setTrackScanResult] = useState(null);
  const [trackFixing, setTrackFixing] = useState(false);

  // Hard Purge state (Danger Zone)
  const [hardPurgeUploadId, setHardPurgeUploadId] = useState("");
  const [hardPurgePreview, setHardPurgePreview] = useState(null);
  const [hardPurgePreviewLoading, setHardPurgePreviewLoading] = useState(false);
  const [hardPurgePassword, setHardPurgePassword] = useState("");
  const [hardPurgeConfirm, setHardPurgeConfirm] = useState("");
  const [hardPurgeRunning, setHardPurgeRunning] = useState(false);

  // Hard Purge by Date (Danger Zone)
  const [hpdFrom, setHpdFrom] = useState("");
  const [hpdTo, setHpdTo] = useState("");
  const [hpdPreview, setHpdPreview] = useState(null);
  const [hpdPreviewLoading, setHpdPreviewLoading] = useState(false);
  const [hpdPassword, setHpdPassword] = useState("");
  const [hpdConfirm, setHpdConfirm] = useState("");
  const [hpdRunning, setHpdRunning] = useState(false);
  // Gate: require a fresh backup before allowing the date-range purge to run.
  // The user clicks "Backup before purge" → a backup file lands on their disk
  // → backupBeforePurgeDone flips to true → only THEN does HARD PURGE enable.
  const [hpdBackupDone, setHpdBackupDone] = useState(false);
  const [hpdBackupRunning, setHpdBackupRunning] = useState(false);
  // Include orphan rows (no date / empty date) — solves the "97% of
  // ledger_entries have no date" problem where the date-range purge can't
  // physically match them. Off by default for safety.
  const [hpdIncludeOrphans, setHpdIncludeOrphans] = useState(false);

  // Customer Name Health state
  const [nameHealth, setNameHealth] = useState(null);
  const [nameHealthLoading, setNameHealthLoading] = useState(false);
  const [nameRelinkRunning, setNameRelinkRunning] = useState(false);

  // Anonymous Sales editor state
  const [anonymousOpen, setAnonymousOpen] = useState(false);
  const [anonymousLoading, setAnonymousLoading] = useState(false);
  const [anonymousItems, setAnonymousItems] = useState([]);
  const [anonymousPage, setAnonymousPage] = useState(1);
  const [anonymousTotal, setAnonymousTotal] = useState(0);
  const [anonymousSearch, setAnonymousSearch] = useState("");
  const [anonymousDraft, setAnonymousDraft] = useState({}); // tracking -> typed name
  const [anonymousSavingTrk, setAnonymousSavingTrk] = useState(null);

  // Garbage Codes scanner state
  const [garbageData, setGarbageData] = useState(null);
  const [garbageLoading, setGarbageLoading] = useState(false);
  const [garbageLedgerOptions, setGarbageLedgerOptions] = useState([]);
  const [garbageRemapTarget, setGarbageRemapTarget] = useState({});
  const [garbageBusyCode, setGarbageBusyCode] = useState(null);
  const [garbagePurgeOpen, setGarbagePurgeOpen] = useState(false);
  const [garbagePurgeCode, setGarbagePurgeCode] = useState("");
  const [garbagePurgePassword, setGarbagePurgePassword] = useState("");
  const [garbagePurgePhrase, setGarbagePurgePhrase] = useState("");
  // Bulk-purge (pure-narration) state
  const [bulkPurgeOpen, setBulkPurgeOpen] = useState(false);
  const [bulkPurgePreview, setBulkPurgePreview] = useState(null);
  const [bulkPurgePreviewLoading, setBulkPurgePreviewLoading] = useState(false);
  const [bulkPurgePassword, setBulkPurgePassword] = useState("");
  const [bulkPurgePhrase, setBulkPurgePhrase] = useState("");
  const [bulkPurgeRunning, setBulkPurgeRunning] = useState(false);


  // Reindex state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [selectedReindexTypes, setSelectedReindexTypes] = useState([]);
  const [reindexTaskId, setReindexTaskId] = useState(null);
  const [reindexProgress, setReindexProgress] = useState(null);
  
  // Available reindex types
  const reindexOptions = [
    { 
      id: "service_detection", 
      label: "Service Detection", 
      description: "Detect shipping service (EMS/RMS) from tracking numbers",
      icon: Truck
    },
    { 
      id: "ledger_creation", 
      label: "Auto-create Ledgers", 
      description: "Create ledger entries for new client codes",
      icon: Users
    },
    { 
      id: "service_propagation", 
      label: "Service Propagation", 
      description: "Propagate service/country info within multi-product parcels",
      icon: Package
    }
  ];

  // Fetch data management status
  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/data-management/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch status:", error);
    }
  };

  // Fetch backups list
  const fetchBackups = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/data-management/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      }
    } catch (error) {
      console.error("Failed to fetch backups:", error);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchBackups(), fetchWipe2025History(), fetchActivityFeed(24), fetchSnapshots(), fetchNameHealth(), fetchGarbageCodes()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Customer Name Health — read-only stats
  const fetchNameHealth = async () => {
    setNameHealthLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/customer-name-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setNameHealth(await r.json());
    } catch (e) {
      // Silent — widget is non-critical
    } finally {
      setNameHealthLoading(false);
    }
  };

  // One-click Re-link from shipment_orders
  const handleNameRelink = async () => {
    if (!window.confirm(
      "Re-link missing customer names on the sales collection from shipment_orders?\n\n" +
      "• Only writes when the field is empty (no overwrites).\n" +
      "• Idempotent — safe to re-run."
    )) return;
    setNameRelinkRunning(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/customer-name-relink`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Re-linked ${d.fixed?.toLocaleString?.() || d.fixed} sales rows (${d.skipped_no_match?.toLocaleString?.() || d.skipped_no_match} truly anonymous).`);
        await fetchNameHealth();
      } else {
        toast.error(d.detail || "Re-link failed");
      }
    } catch (e) {
      toast.error("Re-link failed");
    } finally {
      setNameRelinkRunning(false);
    }
  };

  // Anonymous Sales drill-down
  const fetchAnonymousSales = async (page = 1, search = "") => {
    setAnonymousLoading(true);
    try {
      const token = localStorage.getItem("token");
      const qs = new URLSearchParams({ page: String(page), page_size: "25" });
      if (search && search.length >= 2) qs.set("search", search);
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/anonymous-sales?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setAnonymousItems(d.items || []);
        setAnonymousTotal(d.approx_total || 0);
        setAnonymousPage(d.page || 1);
      } else {
        toast.error("Failed to load anonymous sales");
      }
    } catch (e) {
      toast.error("Failed to load anonymous sales");
    } finally {
      setAnonymousLoading(false);
    }
  };

  const openAnonymousEditor = () => {
    setAnonymousOpen(true);
    setAnonymousDraft({});
    setAnonymousSearch("");
    fetchAnonymousSales(1, "");
  };

  // Garbage codes scanner
  const fetchGarbageCodes = async () => {
    setGarbageLoading(true);
    try {
      const t = localStorage.getItem("token");
      const [scanRes, ledRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/data-management/garbage-codes`, {
          headers: { Authorization: `Bearer ${t}` },
        }),
        fetch(`${BACKEND_URL}/api/ledgers?limit=300`, {
          headers: { Authorization: `Bearer ${t}` },
        }),
      ]);
      if (scanRes.ok) {
        const data = await scanRes.json();
        setGarbageData(data);
        // Auto-pre-fill remap targets with backend's fuzzy suggestions
        const prefill = {};
        (data.items || []).forEach((it) => {
          if (it.suggested_match) prefill[it.code] = it.suggested_match;
        });
        if (Object.keys(prefill).length > 0) {
          setGarbageRemapTarget((prev) => ({ ...prefill, ...prev }));
        }
      }
      if (ledRes.ok) {
        const arr = await ledRes.json();
        const codes = (Array.isArray(arr) ? arr : (arr.ledgers || [])).map(l => l.client_code).filter(Boolean).sort();
        setGarbageLedgerOptions(codes);
      }
    } catch (e) {
      toast.error("Garbage scan failed: " + e.message);
    } finally {
      setGarbageLoading(false);
    }
  };

  const handleGarbageRemap = async (fromCode) => {
    const toCode = garbageRemapTarget[fromCode];
    if (!toCode) { toast.error("Pick a target ledger first"); return; }
    if (!window.confirm(`Remap "${fromCode}" → "${toCode}"?\n\nThis updates every shipment_orders / sales / ledger_entries / payment_followups row.`)) return;
    setGarbageBusyCode(fromCode);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/garbage-codes/remap`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ from_code: fromCode, to_code: toCode }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Remap failed"); return; }
      const total = Object.values(d.counts || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Remapped ${total.toLocaleString()} row(s) from "${fromCode}" → "${toCode}"`);
      fetchGarbageCodes();
    } catch (e) {
      toast.error("Remap failed: " + e.message);
    } finally {
      setGarbageBusyCode(null);
    }
  };

  const openGarbagePurge = (code) => {
    setGarbagePurgeCode(code);
    setGarbagePurgePassword("");
    setGarbagePurgePhrase("");
    setGarbagePurgeOpen(true);
  };

  const handleGarbagePurge = async () => {
    setGarbageBusyCode(garbagePurgeCode);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/garbage-codes/purge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({
          code: garbagePurgeCode,
          password: garbagePurgePassword,
          confirm_phrase: garbagePurgePhrase,
        }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Purge failed"); return; }
      const total = Object.values(d.deleted || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Purged ${total.toLocaleString()} row(s) for "${garbagePurgeCode}"`);
      setGarbagePurgeOpen(false);
      fetchGarbageCodes();
    } catch (e) {
      toast.error("Purge failed: " + e.message);
    } finally {
      setGarbageBusyCode(null);
    }
  };

  const openBulkPurge = async () => {
    setBulkPurgeOpen(true);
    setBulkPurgePassword("");
    setBulkPurgePhrase("");
    setBulkPurgePreview(null);
    setBulkPurgePreviewLoading(true);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/garbage-codes/bulk-purge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ dry_run: true }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Preview failed"); setBulkPurgeOpen(false); return; }
      setBulkPurgePreview(d);
    } catch (e) {
      toast.error("Preview failed: " + e.message);
      setBulkPurgeOpen(false);
    } finally {
      setBulkPurgePreviewLoading(false);
    }
  };

  const handleBulkPurge = async () => {
    if (!bulkPurgePreview || !bulkPurgePreview.codes || bulkPurgePreview.codes.length === 0) return;
    setBulkPurgeRunning(true);
    try {
      const t = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/garbage-codes/bulk-purge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({
          codes: bulkPurgePreview.codes,
          password: bulkPurgePassword,
          confirm_phrase: bulkPurgePhrase,
        }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Bulk purge failed"); return; }
      toast.success(`Purged ${d.total_codes} code(s) · ${d.total_rows_deleted.toLocaleString()} row(s)`);
      setBulkPurgeOpen(false);
      fetchGarbageCodes();
    } catch (e) {
      toast.error("Bulk purge failed: " + e.message);
    } finally {
      setBulkPurgeRunning(false);
    }
  };

  const handleSaveAnonymousName = async (tracking) => {
    const cn = (anonymousDraft[tracking] || "").trim();
    if (cn.length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }
    setAnonymousSavingTrk(tracking);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/anonymous-sales/set-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tracking, customer_name: cn }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Saved "${cn}" → ${d.sales_updated} sales, ${d.shipment_orders_updated} orders`);
        // Drop the just-fixed row from the list locally + clear draft
        setAnonymousItems((prev) => prev.filter((i) => i.tracking !== tracking));
        setAnonymousDraft((prev) => { const c = { ...prev }; delete c[tracking]; return c; });
        // Refresh health stats in the background
        fetchNameHealth();
      } else {
        toast.error(d.detail || "Save failed");
      }
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setAnonymousSavingTrk(null);
    }
  };

  // Create backup
  const handleCreateBackup = async () => {
    if (selectedCollections.length === 0) {
      toast.error("Please select at least one collection to backup");
      return;
    }
    
    setCreatingBackup(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/data-management/backup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ collections: selectedCollections })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Backup created! ${data.total_records} records saved.`);
        await fetchBackups();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create backup");
      }
    } catch (error) {
      toast.error("Failed to create backup");
    } finally {
      setCreatingBackup(false);
    }
  };

  // Restore from backup (DANGEROUS - replaces current data)
  const handleRestore = async (backupId) => {
    // First prompt for password
    const password = window.prompt(
      "🔐 RESTORE PROTECTION\n\n" +
      "Enter the restore password to continue:"
    );
    
    if (!password) {
      return;
    }
    
    // Verify password
    if (password !== "Om@88") {
      toast.error("Incorrect password. Restore cancelled.");
      return;
    }
    
    // Double confirm after password
    const confirm1 = window.confirm(
      "⚠️ WARNING: This will REPLACE your current data with the backup.\n\n" +
      "All changes made after this backup will be LOST.\n\n" +
      "Are you absolutely sure?"
    );
    if (!confirm1) {
      setRestoreConfirmId(null);
      return;
    }
    
    setRestoring(true);
    setRestoreConfirmId(backupId);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/data-management/restore/${backupId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ restore_password: password })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Restored ${data.restored_records || data.total_restored} records from backup!`);
        await fetchStatus();
        await fetchBackups();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to restore backup");
      }
    } catch (error) {
      toast.error("Failed to restore backup");
    } finally {
      setRestoring(false);
      setRestoreConfirmId(null);
    }
  };

  // Download backup as JSON file (for external storage like pendrive)
  const handleDownloadBackup = async () => {
    setDownloading(true);
    setDownloadProgress(0);
    
    // Simulate progress during download
    const progressTimer = setInterval(() => {
      setDownloadProgress(prev => {
        if (prev >= 90) { clearInterval(progressTimer); return 90; }
        return prev + Math.random() * 15;
      });
    }, 500);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/data-management/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ collections: selectedCollections })
      });
      
      if (response.ok) {
        setDownloadProgress(95);
        const blob = await response.blob();
        setDownloadProgress(100);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `eximflow_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);
        toast.success(`Backup downloaded (${sizeMB} MB)! Save to your pendrive for safekeeping.`);
      } else {
        const err = await response.json().catch(() => ({ detail: "Server error during export" }));
        toast.error(err.detail || "Failed to download backup");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to download backup. If data is large, please try again.");
    } finally {
      clearInterval(progressTimer);
      setDownloading(false);
      setTimeout(() => setDownloadProgress(0), 1500);
    }
  };

  // ----- Snapshot Backups -----
  const fetchSnapshots = async () => {
    setSnapshotsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/snapshots`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setSnapshots(d.items || []);
      }
    } catch (e) { /* silent */ }
    finally {
      setSnapshotsLoading(false);
    }
  };

  const handlePurgeExpired = async () => {
    if (!window.confirm("Drop all expired snapshot collections now?")) return;
    setSnapshotsBusy("__purge__");
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/snapshots/purge-expired`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Dropped ${d.dropped_count} expired snapshot(s)`);
        await fetchSnapshots();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Purge failed");
      }
    } catch (e) {
      toast.error("Purge failed");
    } finally {
      setSnapshotsBusy(null);
    }
  };

  const handleHardDeleteSnapshot = async (col) => {
    const password = window.prompt(
      `🧨 Hard-delete snapshot\n\n` +
      `Collection: ${col}\n\n` +
      `This is IRREVERSIBLE — bypasses the 30-day restore window.\n\n` +
      `Enter password to confirm:`
    );
    if (!password) return;
    if (password !== "Om@88") {
      toast.error("Incorrect password.");
      return;
    }
    if (!window.confirm(`Final confirmation — hard-delete ${col}?`)) return;
    setSnapshotsBusy(col);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/snapshots/hard-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password, collection: col }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Dropped ${d.collection} (${d.doc_count} docs)`);
        await fetchSnapshots();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Hard-delete failed");
      }
    } catch (e) {
      toast.error("Hard-delete failed");
    } finally {
      setSnapshotsBusy(null);
    }
  };

  // ----- Recent Activity Feed -----
  const fetchActivityFeed = async (hours = activityHours) => {
    setActivityLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/recent-activity?hours=${hours}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setActivityFeed(d);
      }
    } catch (e) { /* silent */ }
    finally {
      setActivityLoading(false);
    }
  };

  // ----- Targeted 2025 Wipe -----
  const fetchWipe2025History = async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/wipe-2025-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setWipe2025History(d.items || []);
      }
    } catch (e) { /* silent */ }
  };

  const handleWipe2025DryRun = async () => {
    const years = wipe2025Years
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n >= 2000 && n <= 2099);
    if (years.length === 0) {
      toast.error("Enter at least one valid year (2000-2099)");
      return;
    }
    const months = wipe2025Months
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n >= 1 && n <= 12);
    setWipe2025Running(true);
    setWipe2025Counts(null);
    setWipe2025Files(null);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/wipe-2025-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: "Om@88", dry_run: true, years, months }),
      });
      if (r.ok) {
        const d = await r.json();
        setWipe2025Counts(d.counts);
        setWipe2025Files(d.matched_files || null);
        const scope = months.length > 0
          ? `${years.join(", ")} (months ${months.join(", ")})`
          : years.join(", ");
        toast.success(`Dry run for ${scope}: ${d.total_documents_affected} documents would be wiped.`);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Dry run failed");
      }
    } catch (e) {
      toast.error("Dry run failed");
    } finally {
      setWipe2025Running(false);
    }
  };

  const handleWipe2025Execute = async () => {
    const years = wipe2025Years
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n >= 2000 && n <= 2099);
    if (years.length === 0) {
      toast.error("Enter at least one valid year (2000-2099)");
      return;
    }
    const months = wipe2025Months
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n >= 1 && n <= 12);
    const scope = months.length > 0
      ? `years ${years.join(", ")} · months ${months.join(", ")}`
      : `years ${years.join(", ")}`;
    const password = window.prompt(
      `🧨 Targeted Data Wipe — ${scope}\n\n` +
      "This will hard-delete ALL records dated in the selected scope from:\n" +
      "  • sales\n  • payment_followups\n  • ledger_entries\n" +
      "  • partner_ledger_uploads " +
      (months.length > 0
        ? "(cross-period entries stripped by date)\n"
        : "(full + cross-period entries stripped)\n") +
      "\nA snapshot is auto-saved for 30-day restore.\n\nEnter wipe password:"
    );
    if (!password) return;
    if (password !== "Om@88") {
      toast.error("Incorrect password.");
      return;
    }
    if (!window.confirm(`Final confirmation — execute wipe for ${scope} now?`)) return;

    setWipe2025Running(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/wipe-2025-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password, dry_run: false, years, months }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(
          `Wiped ${scope}: ${d.sales_deleted} sales, ${d.payment_followups_deleted} follow-ups, ` +
          `${d.ledger_entries_deleted} ledger entries, ${d.partner_ledger_uploads_full_deleted} files, ` +
          `${d.partner_ledger_uploads_cross_period_entries_stripped} entries stripped from cross-period.`,
          { duration: 8000 }
        );
        setWipe2025Counts(null);
        setWipe2025Files(null);
        await fetchWipe2025History();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Wipe failed");
      }
    } catch (e) {
      toast.error("Wipe failed");
    } finally {
      setWipe2025Running(false);
    }
  };

  const handleWipe2025Restore = async (snapshot_id) => {
    const password = window.prompt(`Restore snapshot ${snapshot_id.slice(0, 8)}...?\n\nEnter password:`);
    if (!password) return;
    setWipe2025RestoringId(snapshot_id);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/restore-wipe-2025`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password, snapshot_id }),
      });
      if (r.ok) {
        const d = await r.json();
        const totals = Object.entries(d.restored || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
        toast.success(`Restored — ${totals}`, { duration: 8000 });
        await fetchWipe2025History();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Restore failed");
      }
    } catch (e) {
      toast.error("Restore failed");
    } finally {
      setWipe2025RestoringId(null);
    }
  };

  // Erase ALL data including backups (EXTREMELY DANGEROUS)
  // Erase All Data — true wipe, preserving ONLY master collections.
  // Replaces the legacy /erase-all endpoint with /erase-all-data which:
  //   1) Has a preview phase showing exactly what will be wiped vs kept
  //   2) Wipes orphan/dateless rows (the surviving 6,213 shipment_orders +
  //      40k ledger_entries that no date-range purge can touch)
  //   3) Preserves master data the user explicitly asked to keep:
  //      ledgers, products, shipping, aliases, price lists & history, users,
  //      role permissions, help/support master.
  const [eraseAllPreview, setEraseAllPreview] = useState(null);
  const [eraseAllPreviewLoading, setEraseAllPreviewLoading] = useState(false);
  const handleEraseAllData = async () => {
    if (!window.confirm(
      "⚠️ TRUE WIPE PREVIEW\n\n" +
      "This will fetch a precise impact report — no data is deleted in this step.\n\n" +
      "Continue to preview?"
    )) return;
    setEraseAllPreviewLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/erase-all-data/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: "{}",
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Preview failed"); return; }
      setEraseAllPreview(d);
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setEraseAllPreviewLoading(false);
    }
  };
  const [eraseAllConfirm, setEraseAllConfirm] = useState("");
  const [eraseAllPassword, setEraseAllPassword] = useState("");
  const [eraseAllBackupDone, setEraseAllBackupDone] = useState(false);
  const handleEraseAllExecute = async () => {
    if (!eraseAllPreview) { toast.error("Run preview first"); return; }
    if (eraseAllConfirm !== "ERASE ALL DATA") { toast.error('Type "ERASE ALL DATA" exactly (case-sensitive)'); return; }
    if (!eraseAllPassword) { toast.error("Enter admin password"); return; }
    if (!eraseAllBackupDone) { toast.error("Download a backup first"); return; }
    const nonZero = Object.entries(eraseAllPreview.to_wipe || {}).filter(([, v]) => v > 0);
    if (!window.confirm(
      `🚨 FINAL WARNING 🚨\n\n` +
      `Permanently erase ${eraseAllPreview.total_to_wipe.toLocaleString()} rows across ${nonZero.length} collections.\n\n` +
      `Top affected:\n${nonZero.slice(0, 8).map(([k, v]) => `  • ${k}: ${v.toLocaleString()}`).join("\n")}\n\n` +
      `Master data preserved: ${eraseAllPreview.keep_collection_count} collections (ledgers, products, shipping, aliases, users, etc.)\n\n` +
      `This cannot be undone.`
    )) return;
    setErasing(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/erase-all-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm_phrase: eraseAllConfirm, password: eraseAllPassword }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Erase failed"); return; }
      toast.success(`✅ Erased ${d.total_deleted.toLocaleString()} rows. Master data preserved.`);
      setEraseAllPreview(null);
      setEraseAllConfirm("");
      setEraseAllPassword("");
      setEraseAllBackupDone(false);
      await fetchStatus();
      await fetchBackups();
    } catch (e) {
      toast.error("Erase failed");
    } finally {
      setErasing(false);
    }
  };

  // Restore from external JSON file (sync from production)
  const handleRestoreFromFile = async () => {
    if (!selectedFile) {
      toast.error("Please select a backup file first");
      return;
    }
    
    // Prompt for password
    const password = window.prompt(
      "IMPORT DATA\n\n" +
      "This will REPLACE the selected collections with the imported data.\n" +
      "Enter the restore password to continue:"
    );
    
    if (!password) {
      return;
    }
    
    if (password !== "Om@88") {
      toast.error("Incorrect password. Import cancelled.");
      return;
    }
    
    // Confirm restore
    const confirm1 = window.confirm(
      "This will REPLACE your current data with the import file.\n\n" +
      `File: ${selectedFile.name}\n` +
      `Size: ${selectedFile.size > 1024*1024 ? (selectedFile.size/(1024*1024)).toFixed(1) + ' MB' : (selectedFile.size/1024).toFixed(1) + ' KB'}\n\n` +
      "Current data in matching collections will be replaced.\n\n" +
      "Continue?"
    );
    
    if (!confirm1) {
      return;
    }
    
    setRestoringExternal(true);
    setRestoreProgress(0);
    setRestoreStatus({ phase: "uploading" });

    try {
      const token = localStorage.getItem("token");
      // For large files (≥ 50 MB) use chunked upload so we don't hit the
      // ingress single-request body cap on the production deployment. For
      // smaller files, the legacy single-shot streaming endpoint is faster
      // (one less round trip per chunk).
      const CHUNK_THRESHOLD_MB = 3;
      // Raw chunk: 2 MB. After base64-encoding (+33% overhead), the JSON
      // request body is ~2.7 MB — well below any production proxy/WAF cap.
      const CHUNK_SIZE = 2 * 1024 * 1024;
      const useChunked = selectedFile.size >= CHUNK_THRESHOLD_MB * 1024 * 1024;
      let response;
      if (useChunked) {
        // --- Chunked upload path -------------------------------------------
        setRestoreStatus({ phase: "uploading", chunked: true, total_size: selectedFile.size });
        // 1) init
        const initResp = await fetch(`${BACKEND_URL}/api/admin/data-management/restore-chunked/init`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: selectedFile.name,
            total_size: selectedFile.size,
            restore_password: password,
          }),
        });
        if (!initResp.ok) {
          let detail = `Restore init failed (HTTP ${initResp.status})`;
          try {
            const text = await initResp.clone().text();
            try { detail = JSON.parse(text).detail || text || detail; } catch { detail = text || detail; }
          } catch (_readErr) { /* body unavailable */ }
          toast.error(detail);
          return;
        }
        const { upload_id } = await initResp.json();
        // 2) append chunks sequentially as base64 JSON (avoids multipart WAF rules)
        const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
        // Helper: convert ArrayBuffer → base64 in a chunked fashion (avoids
        // call stack overflow on big arrays).
        const bufToB64 = (buf) => {
          const bytes = new Uint8Array(buf);
          let binary = '';
          const step = 0x8000; // 32 KB
          for (let p = 0; p < bytes.length; p += step) {
            binary += String.fromCharCode.apply(null, bytes.subarray(p, p + step));
          }
          return btoa(binary);
        };
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
          const slice = selectedFile.slice(start, end);
          const buf = await slice.arrayBuffer();
          const chunk_b64 = bufToB64(buf);
          // Retry each chunk up to 3 times with exponential backoff. This
          // shields us from transient multi-pod glitches (e.g. one pod's
          // MongoDB connection blip) without failing the entire restore.
          let chunkResp = null;
          let lastErr = null;
          for (let attempt = 0; attempt < 3 && !chunkResp; attempt++) {
            if (attempt > 0) {
              await new Promise(r => setTimeout(r, 500 * attempt));
            }
            try {
              const r = await fetch(`${BACKEND_URL}/api/admin/data-management/restore-chunked/append-b64`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ upload_id, chunk_index: i, chunk_b64 }),
              });
              if (r.ok) { chunkResp = r; break; }
              // Server-side error — read the detail; if it's a 4xx fatal
              // (auth/bad payload), abort. If 5xx, retry.
              const errText = await r.clone().text();
              let detail = errText;
              try { detail = JSON.parse(errText).detail || errText; } catch { /* keep raw */ }
              lastErr = `HTTP ${r.status}: ${detail}`;
              if (r.status >= 400 && r.status < 500) { chunkResp = r; break; }
            } catch (netErr) {
              lastErr = `network: ${netErr?.message || netErr}`;
            }
          }
          if (!chunkResp) {
            toast.error(
              `Chunk ${i + 1}/${totalChunks} (${(slice.size / 1024 / 1024).toFixed(2)} MB) ` +
              `failed after 3 attempts. Last error: ${lastErr}`
            );
            return;
          }
          if (!chunkResp.ok) {
            let detail = `HTTP ${chunkResp.status}`;
            try {
              const text = await chunkResp.clone().text();
              try { detail = JSON.parse(text).detail || text || detail; } catch { detail = text || detail; }
            } catch (_readErr) { /* body unavailable */ }
            toast.error(`Chunk ${i + 1}/${totalChunks} failed: ${detail}`);
            return;
          }
          setRestoreStatus({
            phase: "uploading", chunked: true,
            uploaded_bytes: end, total_size: selectedFile.size,
            chunk_index: i + 1, total_chunks: totalChunks,
          });
          setRestoreProgress(Math.min(20, Math.round((end / selectedFile.size) * 20))); // upload = first 20% of bar
        }
        // 3) commit (returns NDJSON progress stream)
        response = await fetch(`${BACKEND_URL}/api/admin/data-management/restore-chunked/commit`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ upload_id }),
        });
      } else {
        // --- Single-shot streaming path (preserves existing behaviour) -----
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("restore_password", password);
        response = await fetch(`${BACKEND_URL}/api/admin/data-management/restore-from-file-stream`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      }

      if (!response.ok) {
        // Server rejected before streaming started (auth, password, 422).
        // Clone the response so we can safely read the body even if the
        // browser has already buffered any preview bytes for some reason
        // (e.g. service workers, dev proxy) — calling .text() on the
        // original Response can throw "body stream already read" in that
        // case. Clone gives us a fresh, independent body stream.
        let detail = `Restore failed (HTTP ${response.status})`;
        try {
          const text = await response.clone().text();
          try { detail = JSON.parse(text).detail || text || detail; } catch { detail = text || detail; }
        } catch (_readErr) {
          // Body unavailable — fall through with status-only message.
        }
        toast.error(detail);
        return;
      }

      // Read NDJSON stream — one JSON event per line
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let totals = { processed: 0, expected: 0 };
      let perCollectionExpected = {};
      let finalSummary = null;

      // Reading phase
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf("\n")) >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }

          if (evt.phase === "error") {
            toast.error(evt.detail || "Restore failed");
            return;
          } else if (evt.phase === "parse") {
            setRestoreStatus({ phase: "parsing", sizeMb: evt.size_mb });
            setRestoreProgress(5);
          } else if (evt.phase === "start") {
            setRestoreStatus({ phase: "starting", collections: evt.collections, skipped: evt.skipped || [] });
            setRestoreProgress(10);
          } else if (evt.phase === "collection_start") {
            perCollectionExpected[evt.collection] = evt.total;
            totals.expected = Object.values(perCollectionExpected).reduce((a, b) => a + b, 0);
            setRestoreStatus({ phase: "restoring", current: evt.collection, total: evt.total, inserted: 0 });
          } else if (evt.phase === "progress") {
            totals.processed = (totals.processed | 0);
            setRestoreStatus(prev => ({
              ...prev,
              phase: "restoring",
              current: evt.collection,
              total: evt.total,
              inserted: evt.inserted,
            }));
            // Overall progress = 10 + 85 * (collection-weighted ratio)
            const totalsoFar = Object.entries(perCollectionExpected).reduce((sum, [c, t]) => {
              if (c === evt.collection) return sum + evt.inserted;
              return sum + (totals.completed?.[c] ?? 0);
            }, 0);
            const expectedSoFar = Object.values(perCollectionExpected).reduce((a, b) => a + b, 0);
            const pct = expectedSoFar > 0 ? Math.min(95, 10 + (85 * totalsoFar) / expectedSoFar) : 10;
            setRestoreProgress(pct);
          } else if (evt.phase === "collection_done") {
            totals.completed = { ...(totals.completed || {}), [evt.collection]: evt.restored };
          } else if (evt.phase === "complete") {
            finalSummary = evt;
            setRestoreProgress(100);
          }
        }
      }

      if (finalSummary) {
        setRestoreStatus({ phase: "done", ...finalSummary });
        toast.success(`Restored ${finalSummary.total_restored.toLocaleString()} records from backup`);
        setSelectedFile(null);
        const fileInput = document.getElementById('external-backup-file');
        if (fileInput) fileInput.value = '';
        await fetchStatus();
        await fetchBackups();
      } else {
        toast.error("Restore stream ended unexpectedly");
      }
    } catch (error) {
      toast.error("Failed to restore from file: " + (error.message || error));
    } finally {
      setRestoringExternal(false);
      setTimeout(() => { setRestoreProgress(0); setRestoreStatus(null); }, 1500);
    }
  };

  // Re-process all errors (SAFE - only touches error records)
  const handleReprocessErrors = async () => {
    setReprocessing(true);
    setReprocessResult(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/errors/recheck/all`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        setReprocessResult(data);
        
        if (data.total_resolved > 0) {
          toast.success(`${data.total_resolved} errors resolved!`);
        } else {
          toast.info("No errors could be auto-resolved. You may need to upload missing data.");
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to reprocess errors");
      }
    } catch (error) {
      toast.error("Failed to reprocess errors");
    } finally {
      setReprocessing(false);
    }
  };

  // Tracking-quality scan: find narrative tracking values
  const handleTrackingScan = async () => {
    setTrackScanLoading(true);
    setTrackScanResult(null);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BACKEND_URL}/api/admin/data-management/tracking-quality-scan`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to scan trackings");
        return;
      }
      const data = await response.json();
      setTrackScanResult(data);
      if (data.total_invalid === 0) {
        toast.success("All tracking values look clean.");
      } else {
        toast.warning(`Found ${data.total_invalid} invalid tracking value(s).`);
      }
    } catch (e) {
      toast.error("Failed to scan trackings");
    } finally {
      setTrackScanLoading(false);
    }
  };

  // Tracking-quality fix: move narrative values to status-note field
  const handleTrackingFix = async () => {
    if (!trackScanResult || trackScanResult.total_invalid === 0) return;
    const pw = window.prompt(
      `Move ${trackScanResult.total_invalid} narrative tracking value(s) to status-note fields and clear the tracking column? Snapshotted for 30 days. Enter password to confirm:`
    );
    if (!pw) return;
    setTrackFixing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BACKEND_URL}/api/admin/data-management/tracking-quality-fix`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ password: pw }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.detail || "Failed to fix trackings");
        return;
      }
      toast.success(
        `Fixed ${data.total_fixed} tracking(s). Snapshot: ${data.snapshot_id.slice(0, 8)}… (restorable 30 days).`
      );
      // Re-scan to refresh state
      await handleTrackingScan();
    } catch (e) {
      toast.error("Failed to fix trackings");
    } finally {
      setTrackFixing(false);
    }
  };


  // Hard Purge by Date — preview
  const handleHpdPreview = async () => {
    if (!hpdFrom || !hpdTo) {
      toast.error("Pick both From and To dates");
      return;
    }
    setHpdPreviewLoading(true);
    setHpdPreview(null);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/hard-purge-by-date/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date_from: hpdFrom, date_to: hpdTo, include_orphans: hpdIncludeOrphans }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Preview failed"); return; }
      setHpdPreview(d);
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setHpdPreviewLoading(false);
    }
  };

  // Hard Purge by Date — execute (irreversible, no snapshot)
  const [hpdVerifyResult, setHpdVerifyResult] = useState(null);
  const handleHpdExecute = async () => {
    if (!hpdPreview) { toast.error("Run preview first"); return; }
    if (hpdConfirm !== "HARD PURGE") { toast.error("Type HARD PURGE exactly (case-sensitive)"); return; }
    if (!hpdPassword) { toast.error("Enter the purge password"); return; }
    if (!hpdBackupDone) { toast.error("Download a backup first (safety net)"); return; }
    // Build a dynamic confirmation listing only collections with > 0 rows.
    const nonZero = Object.entries(hpdPreview.cascade_counts || {})
      .filter(([k, v]) => k !== 'total' && v > 0)
      .sort((a, b) => b[1] - a[1]);
    const summary = nonZero.map(([k, v]) => `  • ${k}: ${v.toLocaleString()}`).join("\n");
    const ok = window.confirm(
      `Permanently delete ALL records dated ${hpdPreview.date_from} → ${hpdPreview.date_to}?\n\n` +
      `${summary}\n\n` +
      `TOTAL: ${(hpdPreview.cascade_counts.total || 0).toLocaleString()} rows across ${nonZero.length} collections.\n\n` +
      `NO snapshot will be taken. This cannot be undone.`
    );
    if (!ok) return;
    setHpdRunning(true);
    setHpdVerifyResult(null);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/hard-purge-by-date`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          date_from: hpdFrom,
          date_to: hpdTo,
          password: hpdPassword,
          confirm_phrase: hpdConfirm,
          include_orphans: hpdIncludeOrphans,
        }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Purge failed"); return; }
      const total = Object.values(d.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Purged ${total.toLocaleString()} rows for ${d.date_from} → ${d.date_to}. Running verification…`);
      // Auto-verify: re-run the preview which counts remaining rows. If all
      // zero the purge was complete; otherwise the user sees exactly what
      // survived (typically nothing, but defensive).
      try {
        const v = await fetch(`${BACKEND_URL}/api/admin/data-management/hard-purge-by-date/preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ date_from: hpdFrom, date_to: hpdTo, include_orphans: hpdIncludeOrphans }),
        });
        if (v.ok) {
          const vData = await v.json();
          const remaining = vData.cascade_counts?.total || 0;
          setHpdVerifyResult({
            success: remaining === 0,
            remaining_total: remaining,
            remaining_by_collection: Object.entries(vData.cascade_counts || {})
              .filter(([k, val]) => k !== 'total' && val > 0)
              .sort((a, b) => b[1] - a[1]),
            verified_at: new Date().toISOString(),
            date_from: hpdFrom,
            date_to: hpdTo,
          });
          if (remaining === 0) {
            toast.success(`✅ Verification PASSED — zero traces left in ${hpdFrom} → ${hpdTo}`);
          } else {
            toast.error(`⚠️ Verification found ${remaining} rows still present — please review.`);
          }
        }
      } catch (_ve) { /* verification optional */ }
      setHpdPreview(null);
      setHpdPassword("");
      setHpdConfirm("");
      setHpdBackupDone(false);
      // Refresh activity feed + name health
      fetchActivityFeed(activityHours);
      fetchNameHealth();
    } catch (e) {
      toast.error("Purge failed");
    } finally {
      setHpdRunning(false);
    }
  };

  // Build the last 6 (month, year) chips for one-click range selection.
  const hpdMonthChips = (() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = d.getMonth(); // 0-11
      const first = `${y}-${String(m + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const last = `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      out.push({ label, from: first, to: last });
    }
    return out;
  })();

  // One-click chip: fill From/To with the chip's range, then auto-preview.
  const applyHpdChip = async (chip) => {
    setHpdFrom(chip.from);
    setHpdTo(chip.to);
    setHpdPreview(null);
    setHpdPreviewLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/admin/data-management/hard-purge-by-date/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date_from: chip.from, date_to: chip.to }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Preview failed"); return; }
      setHpdPreview(d);
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setHpdPreviewLoading(false);
    }
  };

  // Hard Purge — preview cascade before pulling the trigger
  const handleHardPurgePreview = async () => {
    const uid = hardPurgeUploadId.trim();
    if (!uid) {
      toast.error("Enter an upload_id first");
      return;
    }
    setHardPurgePreviewLoading(true);
    setHardPurgePreview(null);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BACKEND_URL}/api/admin/data-management/hard-purge-upload/preview/${encodeURIComponent(uid)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.detail || "Failed to preview");
        return;
      }
      setHardPurgePreview(data);
    } catch (e) {
      toast.error("Failed to preview");
    } finally {
      setHardPurgePreviewLoading(false);
    }
  };

  // Hard Purge — execute irreversible deletion
  const handleHardPurgeExecute = async () => {
    if (!hardPurgePreview) {
      toast.error("Run preview first");
      return;
    }
    if (hardPurgeConfirm !== "HARD PURGE") {
      toast.error('Type the exact phrase "HARD PURGE" to confirm');
      return;
    }
    if (!hardPurgePassword) {
      toast.error("Password required");
      return;
    }
    if (!window.confirm(
      `Permanently delete upload "${hardPurgePreview.upload.filename}"?\n\n` +
      `• ${hardPurgePreview.cascade_counts.ledger_entries} ledger entries\n` +
      `• ${hardPurgePreview.cascade_counts.sales} sales rows\n` +
      `• 1 upload doc + 1 file blob\n\n` +
      `THIS IS PERMANENT. No snapshot. No recovery. Continue?`
    )) return;

    setHardPurgeRunning(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BACKEND_URL}/api/admin/data-management/hard-purge-upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            upload_id: hardPurgePreview.upload.upload_id,
            password: hardPurgePassword,
            confirm_phrase: hardPurgeConfirm,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.detail || "Hard purge failed");
        return;
      }
      const d = data.deleted || {};
      toast.success(
        `Purged "${data.filename}" — ${d.partner_ledger_uploads} upload + ${d.partner_ledger_upload_files} file + ${d.ledger_entries} ledger entries + ${d.sales} sales. IRREVERSIBLE.`
      );
      // Reset all hard-purge inputs
      setHardPurgeUploadId("");
      setHardPurgePreview(null);
      setHardPurgePassword("");
      setHardPurgeConfirm("");
    } catch (e) {
      toast.error("Hard purge failed");
    } finally {
      setHardPurgeRunning(false);
    }
  };

  // Preview reindex changes
  const handlePreviewReindex = async () => {
    if (selectedReindexTypes.length === 0) {
      toast.error("Please select at least one reindex type");
      return;
    }
    
    setPreviewLoading(true);
    setPreviewData(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/data-management/reindex/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ types: selectedReindexTypes })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data.preview);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to generate preview");
      }
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Execute reindex
  const handleExecuteReindex = async () => {
    if (selectedReindexTypes.length === 0) {
      toast.error("Please select at least one reindex type");
      return;
    }
    
    // Confirm backup
    const hasRecentBackup = backups.length > 0 && 
      new Date(backups[0].created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    if (!hasRecentBackup) {
      const confirm = window.confirm(
        "WARNING: You don't have a recent backup. Are you sure you want to proceed? We recommend creating a backup first."
      );
      if (!confirm) return;
    }
    
    setReindexing(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/data-management/reindex/execute`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          types: selectedReindexTypes,
          confirm_backup: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setReindexTaskId(data.task_id);
        toast.info("Reindexing started. This may take a few minutes...");
        
        // Poll for progress
        pollReindexProgress(data.task_id);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to start reindexing");
        setReindexing(false);
      }
    } catch (error) {
      toast.error("Failed to start reindexing");
      setReindexing(false);
    }
  };

  // Poll reindex progress
  const pollReindexProgress = async (taskId) => {
    const token = localStorage.getItem("token");
    
    const checkProgress = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/data-management/reindex/status/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setReindexProgress(data);
          
          if (data.status === "completed") {
            toast.success("Reindexing completed!");
            setReindexing(false);
            setReindexTaskId(null);
            await fetchStatus();
          } else if (data.status === "failed") {
            toast.error("Reindexing failed: " + (data.error || "Unknown error"));
            setReindexing(false);
            setReindexTaskId(null);
          } else {
            // Still in progress, poll again
            setTimeout(checkProgress, 2000);
          }
        }
      } catch (error) {
        console.error("Error polling progress:", error);
        setTimeout(checkProgress, 3000);
      }
    };
    
    checkProgress();
  };

  // Toggle collection for backup
  const toggleCollection = (collection) => {
    setSelectedCollections(prev => 
      prev.includes(collection)
        ? prev.filter(c => c !== collection)
        : [...prev, collection]
    );
  };

  // Toggle reindex type
  const toggleReindexType = (type) => {
    setSelectedReindexTypes(prev => 
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
    setPreviewData(null); // Clear preview when selection changes
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Check admin access
  if (user?.role !== "super_admin" && user?.role !== "admin") {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          Only administrators can access data management features.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="w-6 h-6 text-orange-500" />
          Data Management
        </h1>
        <p className="text-muted-foreground mt-1">
          Safely manage, backup, and fix your database records
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-3xl" data-testid="dm-tabs">
          <TabsTrigger value="overview" data-testid="dm-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="operations" data-testid="dm-tab-operations">Operations</TabsTrigger>
          <TabsTrigger value="sync" data-testid="dm-tab-sync">Sync</TabsTrigger>
          <TabsTrigger value="wipe" data-testid="dm-tab-wipe">Wipe &amp; Backups</TabsTrigger>
          <TabsTrigger value="danger" data-testid="dm-tab-danger" className="text-red-600">Danger Zone</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">

      {/* Safety Notice */}
      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800 dark:text-blue-200">Safe Mode Enabled</AlertTitle>
        <AlertDescription className="text-blue-700 dark:text-blue-300">
          All operations are designed to be safe. Error reprocessing only touches records with errors and 
          will not modify any clean data. We recommend creating a backup before any bulk operations.
        </AlertDescription>
      </Alert>

      {/* What changed today? — Recent Activity Feed */}
      <Card className="border-slate-200 dark:border-slate-700" data-testid="activity-feed-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-4 h-4 text-indigo-500" />
                What changed {activityHours === 24 ? "today" : `in the last ${activityHours}h`}?
                {activityFeed && (
                  <Badge variant="outline" className="text-[11px]">
                    {activityFeed.total} event{activityFeed.total === 1 ? "" : "s"}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                Aggregated from activity logs. Login / page-view noise is excluded.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {[24, 72, 168].map((h) => (
                <Button
                  key={h}
                  size="sm"
                  variant={activityHours === h ? "default" : "outline"}
                  onClick={() => { setActivityHours(h); fetchActivityFeed(h); }}
                  className="h-7 px-2 text-[11px]"
                  data-testid={`activity-range-${h}`}
                >
                  {h === 24 ? "24h" : h === 72 ? "3d" : "7d"}
                </Button>
              ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => fetchActivityFeed(activityHours)}
                disabled={activityLoading}
                className="h-7 w-7 p-0"
                data-testid="activity-refresh"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${activityLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {!activityFeed || activityFeed.total === 0 ? (
            <p className="text-sm text-slate-500 italic">No data events in this window.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {activityFeed.groups.map((g) => {
                  const colorMap = {
                    blue:    "border-blue-300 bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300",
                    amber:   "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
                    red:     "border-red-300 bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300",
                    violet:  "border-violet-300 bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300",
                    emerald: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
                    slate:   "border-slate-300 bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
                  };
                  return (
                    <div
                      key={g.key}
                      className={`rounded-md border px-3 py-2 ${colorMap[g.color] || colorMap.slate}`}
                      data-testid={`activity-group-${g.key}`}
                    >
                      <div className="text-[11px] uppercase tracking-wide opacity-75">{g.label}</div>
                      <div className="text-xl font-semibold leading-tight">{g.count}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center justify-between">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setActivityExpanded((v) => !v)}
                  className="h-7 text-[11px]"
                  data-testid="activity-toggle-details"
                >
                  {activityExpanded ? "Hide details" : "Show recent events"}
                </Button>
                {activityFeed.top_actions?.length > 0 && (
                  <div className="text-[11px] text-slate-500 flex flex-wrap gap-1 justify-end">
                    {activityFeed.top_actions.slice(0, 5).map((a) => (
                      <span key={a.action} className="inline-flex items-center gap-1">
                        <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{a.action}</code>
                        <span className="font-medium">×{a.count}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {activityExpanded && (
                <div className="mt-3 border-t border-slate-200 dark:border-slate-700 pt-3 space-y-3 max-h-96 overflow-y-auto">
                  {activityFeed.groups.map((g) => (
                    <div key={g.key}>
                      <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        {g.label} <span className="text-slate-400">({g.count})</span>
                      </div>
                      <div className="space-y-1">
                        {g.items.map((it) => {
                          const detailsStr = typeof it.details === "string"
                            ? it.details
                            : it.details && typeof it.details === "object"
                              ? JSON.stringify(it.details).slice(0, 200)
                              : "";
                          return (
                            <div
                              key={it.id || `${it.action}-${it.timestamp}`}
                              className="text-[11px] flex flex-wrap items-baseline gap-2 px-2 py-1 rounded bg-slate-50 dark:bg-slate-900/40"
                            >
                              <code className="font-mono text-slate-700 dark:text-slate-300">{it.action}</code>
                              <span className="text-slate-500">by {it.username || "—"}</span>
                              <span className="text-slate-400">{it.timestamp ? new Date(it.timestamp).toLocaleString() : "—"}</span>
                              {detailsStr && <span className="text-slate-600 dark:text-slate-300 truncate max-w-[40ch]" title={detailsStr}>· {detailsStr}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Customer Name Health */}
      <Card className="border-teal-200 dark:border-teal-800" data-testid="customer-name-health-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="w-4 h-4 text-teal-600" />
                Customer Name Health
                {nameHealth && (
                  <Badge variant="outline" className="text-[11px] border-teal-300 text-teal-700 dark:text-teal-300">
                    {nameHealth.coverage_pct}% coverage
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                Sales rows missing a customer name are usually recoverable from <code>shipment_orders</code>.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchNameHealth}
                disabled={nameHealthLoading}
                className="h-7 w-7 p-0"
                data-testid="name-health-refresh"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${nameHealthLoading ? "animate-spin" : ""}`} />
              </Button>
              <Button
                size="sm"
                onClick={handleNameRelink}
                disabled={nameRelinkRunning || !nameHealth || (nameHealth.recoverable_estimate === 0 && nameHealth.missing === 0)}
                className="bg-teal-600 hover:bg-teal-700 text-white h-8"
                data-testid="name-relink-btn"
              >
                {nameRelinkRunning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                Re-link from shipment_orders
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {!nameHealth ? (
            <p className="text-sm text-slate-500 italic">Loading…</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2" data-testid="name-health-total">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Total sales rows</div>
                <div className="text-xl font-semibold leading-tight">{nameHealth.total_sales?.toLocaleString?.() || nameHealth.total_sales}</div>
              </div>
              <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2" data-testid="name-health-populated">
                <div className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">With customer name</div>
                <div className="text-xl font-semibold leading-tight text-emerald-800 dark:text-emerald-200">{nameHealth.populated?.toLocaleString?.() || nameHealth.populated}</div>
              </div>
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2" data-testid="name-health-recoverable">
                <div className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300">Recoverable estimate</div>
                <div className="text-xl font-semibold leading-tight text-amber-800 dark:text-amber-200">{nameHealth.recoverable_estimate?.toLocaleString?.() || nameHealth.recoverable_estimate}</div>
                <div className="text-[10px] text-amber-700/70 dark:text-amber-400/70">from {nameHealth.sampled_trackings?.toLocaleString?.() || nameHealth.sampled_trackings} sampled</div>
              </div>
              <div
                className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-950/50 transition-colors"
                onClick={openAnonymousEditor}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openAnonymousEditor(); }}
                data-testid="name-health-anonymous"
                title="Click to type names for these rows"
              >
                <div className="text-[11px] uppercase tracking-wide text-rose-700 dark:text-rose-300 flex items-center justify-between">
                  <span>Truly anonymous</span>
                  <span className="text-rose-500 text-[10px]">click to edit →</span>
                </div>
                <div className="text-xl font-semibold leading-tight text-rose-800 dark:text-rose-200">{nameHealth.truly_anonymous_estimate?.toLocaleString?.() || nameHealth.truly_anonymous_estimate}</div>
                <div className="text-[10px] text-rose-700/70 dark:text-rose-400/70">no shipment_orders match</div>
              </div>
            </div>
          )}
          {nameHealth?.last_relink && (
            <div className="mt-3 text-[11px] text-slate-500">
              Last re-link: {new Date(nameHealth.last_relink.performed_at).toLocaleString()} · by {nameHealth.last_relink.performed_by_username || "—"}
              {nameHealth.last_relink.details && (
                <> · fixed <span className="font-semibold">{nameHealth.last_relink.details.fixed?.toLocaleString?.() || nameHealth.last_relink.details.fixed}</span></>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Garbage Client Codes scanner */}
      <Card className="border-amber-200 dark:border-amber-800" data-testid="garbage-codes-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Garbage Client Codes
                {garbageData && (
                  <Badge variant="outline" className={`text-[11px] ${garbageData.total_garbage_codes > 0 ? "border-rose-300 text-rose-700" : "border-emerald-300 text-emerald-700"}`}>
                    {garbageData.total_garbage_codes} code(s) · {garbageData.total_orphan_rows.toLocaleString()} orphan row(s)
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                Codes present on <code>shipment_orders</code> that aren't real ledgers (e.g. operator-typed narration garbage).
                Remap each to a real ledger to clean up — or purge irreversibly if the rows are useless.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {garbageData && garbageData.items && garbageData.items.some(it => !it.suggested_match && (it.suspicious_reasons || []).length >= 2) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={openBulkPurge}
                  data-testid="garbage-bulk-purge-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Purge Pure-Narration
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={fetchGarbageCodes} disabled={garbageLoading} className="h-7 w-7 p-0" data-testid="garbage-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${garbageLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
          {garbageData && garbageData.items && garbageData.items.some(it => it.suggested_match) && (
            <div className="flex items-center justify-between bg-sky-50 dark:bg-sky-950/30 border border-sky-200 rounded-md px-3 py-1.5 mt-2 text-[11px]">
              <span className="text-sky-700">
                Auto-suggestions available for {garbageData.items.filter(it => it.suggested_match).length} of {garbageData.items.length} code(s). Pre-filled — review and click Remap.
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[11px] border-sky-300 text-sky-700 hover:bg-sky-100"
                onClick={() => {
                  const prefill = {};
                  garbageData.items.forEach(it => { if (it.suggested_match) prefill[it.code] = it.suggested_match; });
                  setGarbageRemapTarget(prefill);
                  toast.success(`Reset ${Object.keys(prefill).length} target(s) to suggestions`);
                }}
                data-testid="garbage-apply-suggestions"
              >
                Re-apply all suggestions
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {!garbageData ? (
            <p className="text-sm text-slate-500 italic">Loading…</p>
          ) : garbageData.items.length === 0 ? (
            <p className="text-sm text-emerald-700 italic">No garbage codes found. Every <code>shipment_orders</code> client_code maps to a real ledger.</p>
          ) : (
            <div className="border border-amber-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-amber-50 dark:bg-amber-950/30">
                  <tr>
                    <th className="text-left p-2">Code</th>
                    <th className="text-right p-2">Rows</th>
                    <th className="text-left p-2">Reasons</th>
                    <th className="text-left p-2">Samples</th>
                    <th className="text-left p-2 w-[200px]">Remap to</th>
                    <th className="text-right p-2 w-[180px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {garbageData.items.map((it) => {
                    const busy = garbageBusyCode === it.code;
                    const target = garbageRemapTarget[it.code] || "";
                    return (
                      <tr key={it.code} className="border-t border-amber-100 hover:bg-amber-50/40" data-testid={`garbage-row-${it.code}`}>
                        <td className="p-2 font-mono">{it.code}</td>
                        <td className="p-2 text-right font-semibold">{it.count.toLocaleString()}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {it.suspicious_reasons.map(r => (
                              <Badge key={r} variant="outline" className="text-[9px] border-amber-300 text-amber-700">{r.replace("phrase_", "").replace(/_/g, " ")}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-2 text-[10px] text-slate-600 truncate max-w-[180px]" title={`Trackings: ${it.sample_trackings.join(", ")}\nCustomers: ${it.sample_customers.join(", ")}\nDates: ${it.sample_dates.join(", ")}`}>
                          {it.sample_trackings.slice(0, 2).join(", ") || it.sample_customers.slice(0, 2).join(", ") || "—"}
                        </td>
                        <td className="p-2">
                          <select
                            value={target}
                            onChange={(e) => setGarbageRemapTarget(prev => ({ ...prev, [it.code]: e.target.value }))}
                            className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded bg-white dark:bg-slate-900"
                            disabled={busy}
                            data-testid={`garbage-target-${it.code}`}
                          >
                            <option value="">— pick ledger —</option>
                            {garbageLedgerOptions.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          {it.suggested_match && (
                            <div className="mt-1 text-[10px] text-sky-700 flex items-center gap-1" title={it.suggested_reason || ""}>
                              <span>Suggested:</span>
                              <button
                                type="button"
                                className="font-mono font-semibold underline-offset-2 hover:underline"
                                onClick={() => setGarbageRemapTarget(prev => ({ ...prev, [it.code]: it.suggested_match }))}
                                data-testid={`garbage-suggest-${it.code}`}
                              >
                                {it.suggested_match}
                              </button>
                              {typeof it.suggested_score === "number" && (
                                <span className="text-slate-500">({Math.round(it.suggested_score * 100)}%)</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => handleGarbageRemap(it.code)} disabled={busy || !target} className="h-7 text-xs mr-1" data-testid={`garbage-remap-${it.code}`}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Remap"}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => openGarbagePurge(it.code)} disabled={busy} className="h-7 text-xs" data-testid={`garbage-purge-${it.code}`}>
                            Purge
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <SourceAliasesCard />
      <AliasAuditCard />

        </TabsContent>

        <TabsContent value="operations" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Suspicious Order-Date Scanner — admin diagnostic */}
        <Card className="lg:col-span-2 border-amber-200 dark:border-amber-800" data-testid="suspicious-dates-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              🔍 Suspicious Order-Date Scanner
              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">Diagnostic</Badge>
            </CardTitle>
            <CardDescription>
              Finds rows where `order_date == upload date` — likely silent parser fallbacks. Click any tracking below to open it in Shipment Costing for inline correction.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="scan-suspicious-dates-btn"
                onClick={async () => {
                  try {
                    const t = localStorage.getItem("token");
                    const r = await fetch(`${BACKEND_URL}/api/admin/scan-suspicious-dates?limit=2000`, {
                      headers: { Authorization: `Bearer ${t}` },
                    });
                    const d = await r.json();
                    if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
                    setSuspiciousResult(d);
                    if (d.total_flagged === 0) {
                      toast.success("✅ No suspicious dates found — every parcel has a real source date.");
                    } else {
                      toast.warning(`⚠️ ${d.total_flagged} flagged row(s) — see table below`, { duration: 6000 });
                    }
                  } catch (e) {
                    toast.error(`Scan failed: ${e.message}`);
                  }
                }}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                🔍 Run scan
              </Button>
              {suspiciousResult && (
                <span className="text-xs text-slate-600">
                  {suspiciousResult.total_flagged === 0
                    ? "✅ All parcels have real dates"
                    : `⚠️ ${suspiciousResult.total_flagged} flagged · showing first ${(suspiciousResult.sample || []).length}`}
                </span>
              )}
            </div>
            {suspiciousResult && (suspiciousResult.sample || []).length > 0 && (
              <div className="border rounded-lg max-h-96 overflow-auto" data-testid="suspicious-rows-table">
                <table className="w-full text-xs">
                  <thead className="bg-amber-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Tracking</th>
                      <th className="px-2 py-1.5 text-left">Client</th>
                      <th className="px-2 py-1.5 text-left">Order Date</th>
                      <th className="px-2 py-1.5 text-left">Sheet</th>
                      <th className="px-2 py-1.5 text-center">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suspiciousResult.sample.map((r) => (
                      <tr key={r.record_id || r.tracking} className="border-t hover:bg-amber-50/40">
                        <td className="px-2 py-1 font-mono">{r.tracking}</td>
                        <td className="px-2 py-1">{r.client_code}</td>
                        <td className="px-2 py-1 text-rose-600 font-mono">{r.order_date}</td>
                        <td className="px-2 py-1 text-slate-500">{r.sheet_name}</td>
                        <td className="px-2 py-1 text-center">
                          <a
                            href={`/costing?tracking=${encodeURIComponent(r.tracking)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:underline text-[11px]"
                          >
                            Edit →
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Excel-Strip Date Corruption Repair —
            One-button scan + fix for the "030924 typed → Excel stores 30924 →
            old parser interprets as Excel serial → 1984-08-30 → stored as
            300884" corruption. Surfaces every collection that has this
            pattern, shows the proposed corrected date, then applies it
            atomically when the admin password is confirmed. */}
        <Card className="lg:col-span-2 border-rose-200 dark:border-rose-800" data-testid="excel-strip-fix-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-rose-500" />
              🛠 Wrong-Date Repair (Excel-Strip Bug)
              <Badge variant="outline" className="ml-2 text-rose-600 border-rose-300">Smart fix</Badge>
            </CardTitle>
            <CardDescription>
              Repairs the "<b>030924 → 300884</b>" date corruption. Excel strips
              the leading zero on a DDMMYY value, the old parser misreads it as
              a serial-day and stores 1984-08-30. This tool reverses the
              mistake by re-applying the DDMMYY rescue to every corrupted row
              in <span className="font-mono text-[11px]">shipment_orders</span>,
              {" "}<span className="font-mono text-[11px]">partner_ledger_uploads</span>,
              {" "}<span className="font-mono text-[11px]">sales</span> and
              {" "}<span className="font-mono text-[11px]">ledger_entries</span>.
              Click <b>Scan</b> first to preview — nothing is changed until you
              confirm with your admin password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={excelStripBusy}
                data-testid="scan-excel-strip-btn"
                onClick={async () => {
                  setExcelStripBusy(true);
                  try {
                    const t = localStorage.getItem("token");
                    const r = await fetch(`${BACKEND_URL}/api/admin/scan-excel-serial-corrupt-dates?sample_size=5`, {
                      headers: { Authorization: `Bearer ${t}` },
                    });
                    const d = await r.json();
                    if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
                    setExcelStripScan(d);
                    if (d.total_corrupted === 0 && (d.manual_review_required || 0) === 0) {
                      toast.success("✅ Database is clean — no Excel-strip date corruption found.");
                    } else if (d.total_corrupted === 0 && d.manual_review_required > 0) {
                      toast.warning(`⚠️ ${d.manual_review_required} corrupted date(s) need MANUAL review — see "manual review required" rows below. Auto-fixable: 0.`, { duration: 8000 });
                    } else {
                      toast.warning(`⚠️ Found ${d.total_corrupted} auto-fixable + ${d.manual_review_required || 0} manual-review date(s). Review preview, then click Apply Fix.`, { duration: 8000 });
                    }
                  } catch (e) {
                    toast.error(`Scan failed: ${e.message}`);
                  } finally {
                    setExcelStripBusy(false);
                  }
                }}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                🔍 Scan for corrupted dates
              </Button>
              {excelStripScan && excelStripScan.total_corrupted > 0 && (
                <>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    placeholder="Admin password"
                    value={excelStripPassword}
                    onChange={(e) => setExcelStripPassword(e.target.value)}
                    className="w-44 h-8 text-sm"
                    data-testid="excel-strip-password"
                  />
                  <Button
                    size="sm"
                    disabled={excelStripBusy || !excelStripPassword}
                    data-testid="apply-excel-strip-fix-btn"
                    onClick={async () => {
                      if (!window.confirm(`Repair ${excelStripScan.total_corrupted} corrupted date(s)? This rewrites stored DB values across shipment_orders, partner_ledger_uploads, sales, and ledger_entries.`)) return;
                      setExcelStripBusy(true);
                      try {
                        const t = localStorage.getItem("token");
                        const r = await fetch(`${BACKEND_URL}/api/admin/fix-excel-serial-corrupt-dates`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ password: excelStripPassword, dry_run: false }),
                        });
                        const d = await r.json();
                        if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
                        toast.success(`✅ Repaired ${d.total_fixed} corrupted date(s). Refresh affected panels to see the change.`, { duration: 8000 });
                        setExcelStripPassword("");
                        setExcelStripScan(null);
                      } catch (e) {
                        toast.error(`Fix failed: ${e.message}`);
                      } finally {
                        setExcelStripBusy(false);
                      }
                    }}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    {excelStripBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                    🛠 Apply Fix ({excelStripScan.total_corrupted})
                  </Button>
                </>
              )}
              {excelStripScan && (
                <span className="text-xs text-slate-600">
                  {excelStripScan.total_corrupted === 0
                    ? "✅ Database is clean"
                    : `⚠️ ${excelStripScan.total_corrupted} corrupted across ${Object.values(excelStripScan.findings || {}).filter(v => v.count > 0).length} source(s)`}
                </span>
              )}
            </div>

              {excelStripScan && (excelStripScan.total_corrupted > 0 || (excelStripScan.manual_review_required || 0) > 0) && (
                <div className="space-y-3" data-testid="excel-strip-findings">
                  {Object.entries(excelStripScan.findings || {})
                    .filter(([, payload]) => payload.count > 0)
                    .map(([source, payload]) => {
                      const isManual = /manual review/i.test(source);
                      return (
                      <div key={source} className={`border rounded-lg p-3 ${isManual ? "border-amber-300 bg-amber-50/40" : "border-rose-200 bg-rose-50/40"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-mono text-xs font-semibold ${isManual ? "text-amber-800" : "text-rose-800"}`}>{source}</span>
                          <Badge className={isManual ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-rose-100 text-rose-700 border-rose-300"}>
                            {payload.count.toLocaleString()} row{payload.count === 1 ? "" : "s"}
                          </Badge>
                        </div>
                        {isManual && (
                          <p className="text-[11px] text-amber-700 mb-2">
                            ⚠️ These 1990s-era dates can't be unambiguously auto-rescued (the reversed Excel serial doesn't zero-pad to a valid DDMMYY). Fix each row manually via the inline editor in Shipment Costing or Ledger Reconciliation.
                          </p>
                        )}
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead className={isManual ? "bg-amber-100/60" : "bg-rose-100/60"}>
                              <tr>
                                <th className="px-2 py-1 text-left">Tracking / ID</th>
                                <th className="px-2 py-1 text-left">Client / Partner</th>
                                <th className="px-2 py-1 text-left text-rose-600">Current (wrong)</th>
                                <th className="px-2 py-1 text-left text-emerald-700">{isManual ? "Action" : "Proposed (fixed)"}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payload.sample.map((row, i) => {
                                const tracking = row.tracking || row._id || row.record_id || "—";
                                const ccc = row.client_code || row.partner_code || "—";
                                const cur = row.current_date ?? row.order_date ?? row.parcel_dt ?? row.sale_date ?? row.date ?? "—";
                                const prop = isManual
                                  ? (row.reason || "Manual review")
                                  : (row.proposed_date ?? row.proposed_order_date ?? row.proposed_parcel_dt ?? row.proposed_sale_date ?? "—");
                                return (
                                  <tr key={`${source}-${i}`} className={isManual ? "border-t border-amber-200/50" : "border-t border-rose-200/50"}>
                                    <td className="px-2 py-1 font-mono text-slate-700">{String(tracking).slice(0, 32)}</td>
                                    <td className="px-2 py-1">{ccc}</td>
                                    <td className="px-2 py-1 font-mono text-rose-700">{String(cur).slice(0, 22)}</td>
                                    <td className={`px-2 py-1 font-mono ${isManual ? "text-amber-700" : "text-emerald-700"}`}>{String(prop).slice(0, 60)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                          Showing first {payload.sample.length} of {payload.count}{isManual ? " · NOT touched by Apply Fix" : " · Apply Fix will repair all of them"}.
                        </p>
                      </div>
                      );
                    })}
                </div>
              )}
          </CardContent>
        </Card>

        {/* Section 1: Re-process All Errors (Primary Feature) */}
        <Card className="lg:col-span-2 border-orange-200 dark:border-orange-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-500" />
              Re-process All Errors
              <Badge variant="outline" className="ml-2 text-green-600 border-green-300">
                Safe Mode
              </Badge>
            </CardTitle>
            <CardDescription>
              Automatically resolve errors by re-checking against your current data (products, shipping rates, etc.).
              This only affects records with errors - clean data is never touched.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleReprocessErrors}
                disabled={reprocessing}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                size="lg"
                data-testid="reprocess-errors-btn"
              >
                {reprocessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Re-process All Errors
                  </>
                )}
              </Button>
              
              <div className="text-sm text-muted-foreground flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  This will check all error records against your current products, shipping rates, and client data.
                  If the required data now exists, errors will be automatically resolved.
                </span>
              </div>
            </div>
            
            {/* Reprocess Results */}
            {reprocessResult && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Reprocess Results
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Resolved</p>
                    <p className="text-2xl font-bold text-green-600">{reprocessResult.total_resolved}</p>
                  </div>
                  {reprocessResult.results?.shipping && (
                    <div>
                      <p className="text-muted-foreground">Shipping Errors</p>
                      <p className="font-semibold">
                        {reprocessResult.results.shipping.resolved || 0} / {reprocessResult.results.shipping.checked || 0}
                      </p>
                    </div>
                  )}
                  {reprocessResult.results?.products && (
                    <div>
                      <p className="text-muted-foreground">Product Errors</p>
                      <p className="font-semibold">
                        {reprocessResult.results.products.resolved || 0} / {reprocessResult.results.products.checked || 0}
                      </p>
                    </div>
                  )}
                  {reprocessResult.results?.unassigned && (
                    <div>
                      <p className="text-muted-foreground">Unassigned</p>
                      <p className="font-semibold">
                        {reprocessResult.results.unassigned.resolved || 0} / {reprocessResult.results.unassigned.checked || 0}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-blue-500" />
              Database Backup
            </CardTitle>
            <CardDescription>
              Create a snapshot of your data before making changes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Collection Selection */}
            <div>
              <p className="text-sm font-medium mb-2">Select collections to backup:</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  "shipment_orders", "sales", "products", "shipping", "ledgers",
                  "partner_ledger_uploads", "ledger_entries", "notes", "activity_logs",
                  "payment_followups",
                  "client_aliases", "client_price_lists",
                  "client_shipping_rates", "client_price_history",
                  "source_aliases",
                  "advance_payments",
                ].map(collection => (
                  <label 
                    key={collection}
                    className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted transition-colors"
                  >
                    <Checkbox
                      checked={selectedCollections.includes(collection)}
                      onCheckedChange={() => toggleCollection(collection)}
                    />
                    <span className="text-sm capitalize">{collection.replace("_", " ")}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <Button
              onClick={handleCreateBackup}
              disabled={creatingBackup || selectedCollections.length === 0}
              className="w-full"
              data-testid="create-backup-btn"
            >
              {creatingBackup ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Backup...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Create Backup
                </>
              )}
            </Button>
            
            {/* Recent Backups */}
            {backups.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Recent Backups:</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {backups.slice(0, 5).map(backup => (
                    <div 
                      key={backup.id}
                      className="flex items-center justify-between p-3 rounded bg-muted text-sm"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className="font-medium">{new Date(backup.created_at).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{backup.total_records?.toLocaleString()} records</span>
                          <span>•</span>
                          <span>{backup.collections?.join(", ")}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(backup.id)}
                        disabled={restoring}
                        className="ml-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        data-testid={`restore-backup-${backup.id}`}
                      >
                        {restoring && restoreConfirmId === backup.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Restore
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  ⚠️ Restore will replace current data with backup. Use with caution.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Reindex Data */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Reindex Data
            </CardTitle>
            <CardDescription>
              Add missing metadata to existing records (service detection, ledger creation, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reindex Type Selection */}
            <div className="space-y-2">
              {reindexOptions.map(option => (
                <label 
                  key={option.id}
                  className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${
                    selectedReindexTypes.includes(option.id) 
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30' 
                      : 'hover:bg-muted'
                  }`}
                >
                  <Checkbox
                    checked={selectedReindexTypes.includes(option.id)}
                    onCheckedChange={() => toggleReindexType(option.id)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <option.icon className="w-4 h-4 text-purple-500" />
                      <span className="font-medium text-sm">{option.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
            
            {/* Preview & Execute Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handlePreviewReindex}
                disabled={previewLoading || selectedReindexTypes.length === 0}
                className="flex-1"
                data-testid="preview-reindex-btn"
              >
                {previewLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview Changes
              </Button>
              <Button
                onClick={handleExecuteReindex}
                disabled={reindexing || selectedReindexTypes.length === 0}
                className="flex-1"
                data-testid="execute-reindex-btn"
              >
                {reindexing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {reindexProgress?.progress || 0}%
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Execute
                  </>
                )}
              </Button>
            </div>
            
            {/* Preview Results */}
            {previewData && (
              <div className="mt-4 p-3 bg-muted rounded-lg text-sm space-y-3">
                <h4 className="font-semibold">Preview (No changes made yet)</h4>
                
                {previewData.service_detection && (
                  <div>
                    <p className="text-muted-foreground">Service Detection:</p>
                    <p className="font-medium">{previewData.service_detection.would_update} records would be updated</p>
                    {previewData.service_detection.samples?.length > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Sample: {previewData.service_detection.samples[0].tracking} → {previewData.service_detection.samples[0].detected_service}
                      </div>
                    )}
                  </div>
                )}
                
                {previewData.ledger_creation && (
                  <div>
                    <p className="text-muted-foreground">Ledger Creation:</p>
                    <p className="font-medium">{previewData.ledger_creation.would_create} new ledgers would be created</p>
                    {previewData.ledger_creation.new_codes?.length > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Codes: {previewData.ledger_creation.new_codes.slice(0, 5).join(", ")}
                        {previewData.ledger_creation.new_codes.length > 5 && "..."}
                      </div>
                    )}
                  </div>
                )}
                
                {previewData.service_propagation && (
                  <div>
                    <p className="text-muted-foreground">Service Propagation:</p>
                    <p className="font-medium">{previewData.service_propagation.would_update} records would be updated</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Reindex Progress */}
            {reindexProgress && reindexing && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Reindexing in progress...</span>
                  <span className="text-sm">{reindexProgress.progress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${reindexProgress.progress}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tracking Quality Scanner */}
        <Card className="lg:col-span-2 border-rose-200 dark:border-rose-900">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-rose-500" />
              Tracking Quality Scanner
              <Badge variant="outline" className="ml-2 text-rose-600 border-rose-300">
                Data Integrity
              </Badge>
            </CardTitle>
            <CardDescription>
              Scans <code>shipment_orders</code>, <code>sales</code>, and <code>ledger_entries</code> for tracking
              values that look like operator narrative ("Adv. track", "TBD", "tracking pending", "n/a", …) instead of
              real tracking numbers. One-click fix moves the bad value into a status-note field and clears the tracking
              column. Snapshotted for 30-day restore.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleTrackingScan}
                disabled={trackScanLoading || trackFixing}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                data-testid="tracking-quality-scan-btn"
              >
                {trackScanLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
                ) : (
                  <><Eye className="w-4 h-4 mr-2" /> Run scan</>
                )}
              </Button>
              {trackScanResult && trackScanResult.total_invalid > 0 && (
                <Button
                  onClick={handleTrackingFix}
                  disabled={trackFixing || trackScanLoading}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="tracking-quality-fix-btn"
                >
                  {trackFixing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fixing…</>
                  ) : (
                    <>Fix {trackScanResult.total_invalid} row(s)</>
                  )}
                </Button>
              )}
            </div>

            {trackScanResult && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  trackScanResult.total_invalid === 0
                    ? "bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900"
                    : "bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900"
                }`}
                data-testid="tracking-quality-result"
              >
                {trackScanResult.total_invalid === 0 ? (
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <CheckCircle className="w-4 h-4" />
                    All tracking values look clean across all 3 collections.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="font-medium text-rose-700 dark:text-rose-300">
                      Found {trackScanResult.total_invalid} invalid tracking value(s):
                    </div>
                    <ul className="space-y-2 ml-4">
                      {trackScanResult.issues.map((iss) => (
                        <li key={iss.collection}>
                          <div className="font-mono text-xs">
                            <span className="font-semibold">{iss.collection}.{iss.field}</span>: {iss.invalid_count} row(s)
                          </div>
                          <ul className="ml-4 mt-1 space-y-0.5">
                            {iss.samples.map((s, idx) => (
                              <li key={idx} className="text-xs text-muted-foreground">
                                <code className="bg-white dark:bg-slate-800 px-1 rounded">{s.value}</code>
                                {s.client_code && (
                                  <span className="ml-2">— {s.client_code}{s.customer_name ? ` · ${s.customer_name}` : ""}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-2 text-[10px] text-muted-foreground">
                  Scanned at: {new Date(trackScanResult.scanned_at).toLocaleString()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="sync" className="space-y-6 mt-6">

      {/* Database Stats */}
      {status && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Current Database Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-xs text-muted-foreground">Shipment Orders</p>
                <p className="text-xl font-bold">{status.shipment_orders_count?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-xs text-muted-foreground">Pending Errors</p>
                <p className="text-xl font-bold text-orange-500">{status.pending_errors?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-xs text-muted-foreground">Products</p>
                <p className="text-xl font-bold">{status.products_count?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-xs text-muted-foreground">Ledger Accounts</p>
                <p className="text-xl font-bold">{status.ledgers_count?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* External Backup / Data Sync - Download */}
      <Card className="border-green-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-green-500" />
            Export Data (For Sync / Backup)
          </CardTitle>
          <CardDescription>
            Download your full database as JSON. Use this to sync data between production and testing environments, or save to external storage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleDownloadBackup}
              disabled={downloading || selectedCollections.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="download-backup-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing Download...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Download Backup to File
                </>
              )}
            </Button>
            <p className="text-sm text-muted-foreground">
              Collections: {selectedCollections.join(", ")}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Export includes all selected collections. File can be imported into any EximFlow environment.
          </p>
          {/* Download Progress Bar */}
          {downloading && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Preparing backup...</span>
                <span>{Math.round(downloadProgress)}%</span>
              </div>
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all duration-300"
                  style={{ width: `${downloadProgress}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import / Restore from File */}
      <Card className="border-blue-300 dark:border-blue-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5 text-blue-500" />
            Import Data (Sync from Production)
          </CardTitle>
          <CardDescription>
            Upload a JSON export file from your production app to sync data into this environment for testing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <input
                type="file"
                id="external-backup-file"
                accept=".json"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  dark:file:bg-blue-950 dark:file:text-blue-300"
              />
            </div>
            
            {selectedFile && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Selected: {selectedFile.name}
                </p>
                <p className="text-xs text-blue-600/70 dark:text-blue-400/70">
                  Size: {selectedFile.size > 1024 * 1024 
                    ? `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`
                    : `${(selectedFile.size / 1024).toFixed(2)} KB`}
                </p>
              </div>
            )}
            
            <Button
              onClick={handleRestoreFromFile}
              disabled={restoringExternal || !selectedFile}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
              data-testid="restore-from-file-btn"
            >
              {restoringExternal ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Restore from File
                </>
              )}
            </Button>
            
            {/* Restore Progress Bar */}
            {restoringExternal && (
              <div className="space-y-1.5 w-full">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {restoreStatus?.phase === "uploading" && "Uploading file…"}
                    {restoreStatus?.phase === "parsing" && `Parsing JSON (${restoreStatus.sizeMb} MB)…`}
                    {restoreStatus?.phase === "starting" && `Starting restore of ${restoreStatus.collections?.length || 0} collection(s)…`}
                    {restoreStatus?.phase === "restoring" && (
                      <>Restoring <b className="text-blue-600">{restoreStatus.current}</b>
                       {restoreStatus.total ? ` — ${(restoreStatus.inserted || 0).toLocaleString()} / ${restoreStatus.total.toLocaleString()}` : ""}</>
                    )}
                    {restoreStatus?.phase === "done" && (
                      <>Done — restored <b>{restoreStatus.total_restored?.toLocaleString()}</b> records</>
                    )}
                    {!restoreStatus && "Working…"}
                  </span>
                  <span>{Math.round(restoreProgress)}%</span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-300"
                    style={{ width: `${restoreProgress}%` }}
                  />
                </div>
                {restoreStatus?.phase === "starting" && (restoreStatus.skipped?.length > 0) && (
                  <p className="text-[11px] text-amber-700">
                    Skipped (not in allow-list): {restoreStatus.skipped.join(", ")}
                  </p>
                )}
              </div>
            )}
          </div>
          
          <Alert className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700 dark:text-blue-300 text-sm">
              This will replace ALL current data with the backup file. Password will be required.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="wipe" className="space-y-6 mt-6">

      {/* Targeted Data Wipe (by year) */}
      <Card className="border-amber-300 dark:border-amber-800">
        <CardHeader className="bg-amber-50 dark:bg-amber-950/30 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-5 h-5" />
            Targeted Data Wipe (by Year)
          </CardTitle>
          <CardDescription className="text-amber-700/80 dark:text-amber-400/80">
            Hard-delete records dated in selected year(s) from Sales / Partner Ledger / Follow-ups — 30-day restore guaranteed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <Alert className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
              Scope: <code>sales</code> (sale_date matches year), <code>payment_followups</code> (year field),
              {" "}<code>ledger_entries</code> (date suffix), <code>shipment_orders</code> (order_date — clears
              {" "}No-Tracking / Costing-Error rows), <code>partner_ledger_uploads</code> (purely-year files;
              cross-period files have only the selected-year entries stripped).
              Orphan <code>upload_audits</code> are also swept. Every wipe creates a restorable snapshot (30 days).
            </AlertDescription>
          </Alert>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1">
                Year(s) — comma-separated
              </label>
              <input
                type="text"
                value={wipe2025Years}
                onChange={(e) => setWipe2025Years(e.target.value)}
                placeholder="2025  or  2024,2025"
                className="w-40 px-3 py-2 text-sm border border-amber-300 rounded-md bg-white dark:bg-slate-900 dark:border-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="wipe-years-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">e.g. <code>2024</code> or <code>2024,2025</code></div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1">
                Month(s) 1-12 — optional
              </label>
              <input
                type="text"
                value={wipe2025Months}
                onChange={(e) => setWipe2025Months(e.target.value)}
                placeholder="(all)  or  1,2,3"
                className="w-40 px-3 py-2 text-sm border border-amber-300 rounded-md bg-white dark:bg-slate-900 dark:border-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="wipe-months-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Blank = whole year. <code>1,2,3</code> = Q1. Files skipped when months set.
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleWipe2025DryRun}
              disabled={wipe2025Running}
              className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-300"
              data-testid="wipe-2025-dryrun-btn"
            >
              {wipe2025Running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Dry-Run Preview
            </Button>
            <Button
              onClick={handleWipe2025Execute}
              disabled={wipe2025Running}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="wipe-2025-execute-btn"
            >
              {wipe2025Running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Execute Wipe
            </Button>
          </div>

          {wipe2025Counts && (
            <div className="rounded-lg border border-amber-300 bg-amber-50/40 dark:bg-amber-950/20 p-3">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">Dry-run preview</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                <div><span className="font-semibold">sales:</span> {wipe2025Counts.sales}</div>
                <div><span className="font-semibold">followups:</span> {wipe2025Counts.payment_followups}</div>
                <div><span className="font-semibold">ledger entries:</span> {wipe2025Counts.ledger_entries}</div>
                <div data-testid="wipe-dryrun-shipment-orders"><span className="font-semibold">shipment orders:</span> {wipe2025Counts.shipment_orders ?? 0}</div>
                <div><span className="font-semibold">files (full):</span> {wipe2025Counts.partner_ledger_uploads_full}</div>
                <div><span className="font-semibold">cross-period:</span> {wipe2025Counts.partner_ledger_uploads_cross_period}</div>
              </div>
              {wipe2025Files && (wipe2025Files.full?.length > 0 || wipe2025Files.cross_period?.length > 0) && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-amber-700 dark:text-amber-300 font-medium">
                    Matched files ({(wipe2025Files.full?.length || 0) + (wipe2025Files.cross_period?.length || 0)}) — click to review
                  </summary>
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto bg-white dark:bg-slate-900/50 rounded p-2 border border-slate-200 dark:border-slate-700">
                    {wipe2025Files.full?.map((f) => (
                      <div key={f.upload_id} className="text-slate-700 dark:text-slate-300">
                        <Badge variant="outline" className="mr-2 text-[10px]">FULL</Badge>
                        {f.filename}
                      </div>
                    ))}
                    {wipe2025Files.cross_period?.map((f) => (
                      <div key={f.upload_id} className="text-slate-700 dark:text-slate-300">
                        <Badge variant="outline" className="mr-2 text-[10px] border-blue-300 text-blue-700">CROSS</Badge>
                        {f.filename}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {wipe2025History.length > 0 && (
            <div className="mt-4 border-t border-amber-200 pt-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Recent wipes (restorable within 30 days)
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {wipe2025History.map((w) => {
                  const s = w.summary || {};
                  const total = (s.sales_deleted || 0) + (s.payment_followups_deleted || 0) +
                                (s.ledger_entries_deleted || 0) + (s.shipment_orders_deleted || 0) +
                                (s.partner_ledger_uploads_full_deleted || 0) +
                                (s.partner_ledger_uploads_cross_period_entries_stripped || 0);
                  const yrs = s.years && s.years.length ? s.years.join(", ") : "2025";
                  const mths = s.months && s.months.length ? ` · M:${s.months.join(",")}` : "";
                  return (
                    <div
                      key={w.snapshot_id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-white dark:bg-slate-900/50 dark:border-slate-700 p-2 text-xs"
                      data-testid={`wipe-2025-history-${w.snapshot_id}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-mono text-[11px] text-slate-500">{w.snapshot_id}</span>
                        <span className="text-slate-700 dark:text-slate-200">
                          <Badge variant="outline" className="mr-1 text-[10px]">{yrs}{mths}</Badge>
                          {new Date(w.performed_at).toLocaleString()} · by {w.performed_by_username || "—"} ·{" "}
                          <span className="font-semibold">{total}</span> documents
                        </span>
                        <span className="text-[11px] text-slate-500">
                          sales {s.sales_deleted || 0} · followups {s.payment_followups_deleted || 0} ·{" "}
                          ledger {s.ledger_entries_deleted || 0} · orders {s.shipment_orders_deleted || 0} ·{" "}
                          files {s.partner_ledger_uploads_full_deleted || 0} ·{" "}
                          cross-stripped {s.partner_ledger_uploads_cross_period_entries_stripped || 0}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {w.is_restored ? (
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                            Restored
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleWipe2025Restore(w.snapshot_id)}
                            disabled={wipe2025RestoringId === w.snapshot_id}
                            data-testid={`wipe-2025-restore-${w.snapshot_id}`}
                          >
                            {wipe2025RestoringId === w.snapshot_id ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <RotateCcw className="w-3 h-3 mr-1" />
                            )}
                            Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backups (snapshot inventory) */}
      <Card className="border-slate-200 dark:border-slate-700" data-testid="backups-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Archive className="w-4 h-4 text-indigo-500" />
                Backups
                <Badge variant="outline" className="text-[11px]">
                  {snapshots.length}
                </Badge>
                {snapshots.filter((s) => s.expired).length > 0 && (
                  <Badge variant="outline" className="text-[11px] border-amber-300 text-amber-700">
                    {snapshots.filter((s) => s.expired).length} expired
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                Snapshot collections from wipes / date normalization / tracking-case normalization.
                Auto-purged 30 days after creation.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handlePurgeExpired}
                disabled={snapshotsBusy === "__purge__"}
                className="h-7 text-[11px] border-amber-400 text-amber-700 hover:bg-amber-50"
                data-testid="snapshots-purge-expired-btn"
              >
                {snapshotsBusy === "__purge__" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Purge expired
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchSnapshots}
                disabled={snapshotsLoading}
                className="h-7 w-7 p-0"
                data-testid="snapshots-refresh"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${snapshotsLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {snapshots.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No snapshot collections.</p>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSnapshotsExpanded((v) => !v)}
                className="h-7 text-[11px] mb-2"
                data-testid="snapshots-toggle"
              >
                {snapshotsExpanded ? "Hide list" : `Show ${snapshots.length} snapshot collection${snapshots.length === 1 ? "" : "s"}`}
              </Button>

              {snapshotsExpanded && (
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {snapshots.map((s) => {
                    const statusBadge = s.expired
                      ? { label: "Expired", cls: "border-red-300 text-red-700" }
                      : s.is_restored
                        ? { label: "Restored", cls: "border-emerald-300 text-emerald-700" }
                        : { label: "Active", cls: "border-blue-300 text-blue-700" };
                    return (
                      <div
                        key={s.collection}
                        className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2 text-xs"
                        data-testid={`snapshot-row-${s.collection}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] ${statusBadge.cls}`}>{statusBadge.label}</Badge>
                            <code className="font-mono text-[11px] text-slate-700 dark:text-slate-300 truncate" title={s.collection}>
                              {s.collection}
                            </code>
                            <span className="text-slate-500">{s.doc_count} docs</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {s.action} · by {s.performed_by_username || "—"} ·{" "}
                            {s.performed_at ? new Date(s.performed_at).toLocaleString() : "—"}
                            {s.expires_at && (
                              <> · expires {new Date(s.expires_at).toLocaleDateString()}</>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleHardDeleteSnapshot(s.collection)}
                          disabled={snapshotsBusy === s.collection}
                          className="h-7 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`snapshot-hard-delete-${s.collection}`}
                          title="Hard delete (password required)"
                        >
                          {snapshotsBusy === s.collection ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3 mr-1" />
                          )}
                          Hard delete
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="danger" className="space-y-6 mt-6">

      {/* Hard Purge Upload — irreversible, no snapshot */}
      <Card className="border-red-300 dark:border-red-800" data-testid="hard-purge-card">
        <CardHeader className="bg-red-50 dark:bg-red-950/30 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="w-5 h-5" />
            Hard Purge Upload
            <Badge variant="outline" className="ml-2 text-red-700 border-red-400 bg-red-100/60">
              No Recovery
            </Badge>
          </CardTitle>
          <CardDescription className="text-red-700/80">
            Permanently delete an upload and every cascaded row (ledger entries, sales, file blob) across all
            collections. <strong>No snapshot is taken. Data cannot be restored.</strong> Use only when you are
            absolutely sure the upload should never appear in any view again.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>This action is irreversible</AlertTitle>
            <AlertDescription>
              Unlike the soft-delete options above, hard purge skips the 30-day snapshot. Once executed,
              the data is gone forever — it will not be fetched by Payment Verification, Ledger Account,
              Pending Sales, or any other view.
            </AlertDescription>
          </Alert>

          {/* Step 1: Upload ID + Preview */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-red-700">1. Upload ID</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={hardPurgeUploadId}
                onChange={(e) => { setHardPurgeUploadId(e.target.value); setHardPurgePreview(null); }}
                placeholder="e.g. 83969182-dd3f-457f-b9d1-0fefbb813f27"
                className="flex-1 px-3 py-2 text-sm font-mono border border-red-200 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="hard-purge-upload-id-input"
              />
              <Button
                variant="outline"
                onClick={handleHardPurgePreview}
                disabled={hardPurgePreviewLoading || !hardPurgeUploadId.trim()}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="hard-purge-preview-btn"
              >
                {hardPurgePreviewLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                ) : (
                  <><Eye className="w-4 h-4 mr-2" /> Preview cascade</>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Tip: copy an <code>upload_id</code> from the Payment Verification → Uploaded Sheets tab.
            </p>
          </div>

          {/* Step 2: Cascade preview */}
          {hardPurgePreview && (
            <div className="space-y-2 p-3 border border-red-300 rounded-md bg-red-50 dark:bg-red-950/30" data-testid="hard-purge-preview">
              <div className="text-sm font-semibold text-red-700">
                Cascade preview — what will be deleted:
              </div>
              <div className="text-xs space-y-1 text-red-800 dark:text-red-300">
                <div><strong>File:</strong> {hardPurgePreview.upload.filename}</div>
                <div><strong>Uploaded:</strong> {new Date(hardPurgePreview.upload.uploaded_at).toLocaleString()} by {hardPurgePreview.upload.uploaded_by || "—"}</div>
                <div><strong>Partner(s):</strong> {hardPurgePreview.upload.partner_codes.join(", ") || "—"}</div>
                <div><strong>Trackings affected:</strong> {hardPurgePreview.tracking_count}</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {Object.entries(hardPurgePreview.cascade_counts).map(([k, v]) => (
                  <div key={k} className="bg-white dark:bg-slate-900 border border-red-200 rounded p-2 text-center">
                    <div className="text-lg font-bold text-red-700">{v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-red-600/70 font-mono">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Confirm + execute */}
          {hardPurgePreview && (
            <div className="space-y-3 p-3 border-2 border-red-500 rounded-md bg-red-100/40 dark:bg-red-950/40">
              <div>
                <label className="text-sm font-medium text-red-700">2. Type <code className="bg-red-200 px-1 rounded">HARD PURGE</code> exactly:</label>
                <input
                  type="text"
                  value={hardPurgeConfirm}
                  onChange={(e) => setHardPurgeConfirm(e.target.value)}
                  placeholder="HARD PURGE"
                  className="mt-1 w-full px-3 py-2 text-sm font-mono border border-red-300 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="hard-purge-confirm-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-red-700">3. Admin password:</label>
                <input
                  type="password"
                  value={hardPurgePassword}
                  onChange={(e) => setHardPurgePassword(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-red-300 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="hard-purge-password-input"
                />
              </div>
              <Button
                variant="destructive"
                onClick={handleHardPurgeExecute}
                disabled={hardPurgeRunning || hardPurgeConfirm !== "HARD PURGE" || !hardPurgePassword}
                className="w-full bg-red-700 hover:bg-red-800 text-white"
                data-testid="hard-purge-execute-btn"
              >
                {hardPurgeRunning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Purging…</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" /> Permanently purge this upload</>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hard Purge by Date Range — irreversible, no snapshot */}
      <Card className="border-red-300 dark:border-red-800" data-testid="hard-purge-date-card">
        <CardHeader className="bg-red-50 dark:bg-red-950/30 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="w-5 h-5" />
            Hard Purge by Date Range
            <Badge variant="outline" className="ml-2 text-red-700 border-red-400 bg-red-100/60">
              No Recovery
            </Badge>
          </CardTitle>
          <CardDescription className="text-red-700/80">
            Permanently delete every row dated within a chosen range across <code>sales</code>,{" "}
            <code>shipment_orders</code>, <code>ledger_entries</code>, <code>payment_followups</code>,{" "}
            <code>partner_ledger_uploads</code>, file blobs, and <code>advance_payments</code>.
            <strong> No snapshot is taken — data cannot be restored.</strong> Need restore? Use Targeted
            Wipe by Year in the Wipe &amp; Backups tab instead.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>This action is irreversible</AlertTitle>
            <AlertDescription>
              Skips the 30-day snapshot. Use when you are absolutely sure the range should never reappear
              in any view. Both password and the exact phrase are required.
            </AlertDescription>
          </Alert>

          {/* Step 1: Pick range + preview */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-red-700">1. Date range</label>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="hpd-month-chips">
              <span className="text-[11px] text-slate-500 mr-1">Quick pick:</span>
              {hpdMonthChips.map((chip) => {
                const active = hpdFrom === chip.from && hpdTo === chip.to;
                return (
                  <button
                    key={chip.label}
                    type="button"
                    onClick={() => applyHpdChip(chip)}
                    disabled={hpdPreviewLoading}
                    className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                      active
                        ? "bg-red-600 text-white border-red-700"
                        : "border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                    } ${hpdPreviewLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`hpd-chip-${chip.label.replace(/\s+/g, "-")}`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <div className="text-xs text-slate-500 mb-1">From</div>
                <input
                  type="date"
                  value={hpdFrom}
                  onChange={(e) => { setHpdFrom(e.target.value); setHpdPreview(null); setHpdBackupDone(false); }}
                  className="px-3 py-2 text-sm border border-red-200 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="hpd-from-input"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">To</div>
                <input
                  type="date"
                  value={hpdTo}
                  onChange={(e) => { setHpdTo(e.target.value); setHpdPreview(null); setHpdBackupDone(false); }}
                  className="px-3 py-2 text-sm border border-red-200 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="hpd-to-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleHpdPreview}
                disabled={hpdPreviewLoading || !hpdFrom || !hpdTo}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="hpd-preview-btn"
              >
                {hpdPreviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Preview cascade
              </Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 cursor-pointer" data-testid="hpd-include-orphans-label">
              <input
                type="checkbox"
                checked={hpdIncludeOrphans}
                onChange={(e) => { setHpdIncludeOrphans(e.target.checked); setHpdPreview(null); setHpdBackupDone(false); }}
                className="h-3.5 w-3.5"
                data-testid="hpd-include-orphans"
              />
              <span>
                <strong>Include orphan rows</strong> (no date / empty date) — necessary to sweep
                dateless <code>ledger_entries</code> and <code>shipment_orders</code> that survive a normal date-range purge.
                Use this when you want zero traces.
              </span>
            </label>
            <p className="text-[11px] text-slate-500">
              Tip: dates are inclusive on both ends and apply to <code>sale_date</code>,{" "}
              <code>order_date</code>, <code>ledger_entries.date</code>, file <code>uploaded_at</code>,{" "}
              and <code>advance_payments.created_at</code>.
            </p>
          </div>

          {/* Step 2: Preview counts */}
          {hpdPreview && (
            <div className="space-y-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-300 rounded-md" data-testid="hpd-preview-panel">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(hpdPreview.cascade_counts).filter(([k]) => k !== "total").map(([k, v]) => (
                  <div key={k} className="bg-white dark:bg-slate-900 border border-red-200 rounded p-2 text-center">
                    <div className="text-lg font-bold text-red-700">{(v || 0).toLocaleString()}</div>
                    <div className="text-[10px] uppercase tracking-wider text-red-600/70 font-mono">{k}</div>
                  </div>
                ))}
              </div>
              <div className="text-sm text-red-700 font-medium" data-testid="hpd-preview-total">
                Total rows to delete: <span className="font-bold">{(hpdPreview.cascade_counts.total || 0).toLocaleString()}</span> · range {hpdPreview.date_from} → {hpdPreview.date_to}
              </div>
              {/* Per-collection breakdown — only show non-zero counts so the
                  user instantly sees which areas of the app will be cleaned. */}
              <div className="mt-2 max-h-40 overflow-y-auto bg-white/60 border border-red-200 rounded p-2">
                <div className="font-semibold text-[11px] text-red-900 mb-1">Per-collection breakdown:</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-mono">
                  {Object.entries(hpdPreview.cascade_counts || {})
                    .filter(([k, v]) => k !== 'total' && v > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-red-100/50 py-0.5">
                        <span className="text-slate-700">{k.replace(/_/g, ' ')}</span>
                        <span className="font-bold text-red-700">{v.toLocaleString()}</span>
                      </div>
                    ))}
                </div>
              </div>
              {/* Reassurance: only date-bound transactional tables are affected.
                  Master/static data (price catalogues, aliases, etc.) stay safe. */}
              <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-[11px] text-emerald-800 leading-relaxed" data-testid="hpd-safe-collections-note">
                <div className="font-semibold text-emerald-900 mb-1">✅ Will NOT delete (master data is safe):</div>
                <div className="font-mono text-[10px]">
                  Products · Shipping price list · Client Price Lists · Client Shipping Rates · Client Price History · Client Aliases · Source Aliases · Partner Ledger Master · Users · Reports/Tickets/Help master data
                </div>
                <div className="font-semibold text-emerald-900 mt-2 mb-1">🧹 WILL purge (clean break across the app):</div>
                <div className="font-mono text-[10px]">
                  Sales · Shipment Orders · Ledger Entries · Manual Ledger Debits/Payments · Payment Follow-ups · CRM activities + follow-ups · Partner Ledger Uploads + audit logs · Raw Funds rows + uploads · Advance Payments · Order staging · Snapshot backups in range
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm + execute */}
          {hpdPreview && (
            <div className="space-y-3 p-3 border-2 border-red-500 rounded-md bg-red-100/40 dark:bg-red-950/40">
              {/* Step 3a — mandatory: download a fresh backup BEFORE purge.
                  The HARD PURGE button stays disabled until the backup file
                  has actually been generated and downloaded to the user's
                  device. Two-second insurance against a wrong year selection. */}
              <div>
                <label className="text-sm font-medium text-red-700">1. Download a fresh full backup (safety net):</label>
                <Button
                  variant="outline"
                  onClick={async () => {
                    setHpdBackupRunning(true);
                    try {
                      await handleDownloadBackup();
                      setHpdBackupDone(true);
                      toast.success("Backup saved. You can now run HARD PURGE.");
                    } finally {
                      setHpdBackupRunning(false);
                    }
                  }}
                  disabled={hpdBackupRunning || hpdBackupDone}
                  className={`mt-1 w-full ${hpdBackupDone ? 'border-emerald-400 text-emerald-700 bg-emerald-50' : 'border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100'}`}
                  data-testid="hpd-backup-before-purge-btn"
                >
                  {hpdBackupRunning ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating backup…</>
                  ) : hpdBackupDone ? (
                    <>✅ Backup downloaded — proceed</>
                  ) : (
                    <>🛟 Backup before purge (download .json)</>
                  )}
                </Button>
                {!hpdBackupDone && (
                  <p className="text-[10px] text-red-700/80 mt-1">
                    HARD PURGE button stays disabled until you click this and the backup file lands on your disk.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-red-700">2. Type <code className="bg-red-200 px-1 rounded">HARD PURGE</code> exactly:</label>
                <input
                  type="text"
                  value={hpdConfirm}
                  onChange={(e) => setHpdConfirm(e.target.value)}
                  placeholder="HARD PURGE"
                  className="mt-1 w-full px-3 py-2 text-sm font-mono border border-red-300 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="hpd-confirm-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-red-700">3. Admin password:</label>
                <input
                  type="password"
                  value={hpdPassword}
                  onChange={(e) => setHpdPassword(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-red-300 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="hpd-password-input"
                />
              </div>
              <Button
                variant="destructive"
                onClick={handleHpdExecute}
                disabled={hpdRunning || hpdConfirm !== "HARD PURGE" || !hpdPassword || !hpdBackupDone}
                className="w-full bg-red-700 hover:bg-red-800 text-white"
                data-testid="hpd-execute-btn"
              >
                {hpdRunning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Purging…</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" /> Permanently purge this date range</>
                )}
              </Button>
            </div>
          )}

          {/* Verify Clean panel — appears after a successful purge so the
              user has a final visual confirmation that no traces survived
              across the 22 cascaded collections. */}
          {hpdVerifyResult && (
            <div
              className={`p-3 rounded-md border-2 ${hpdVerifyResult.success ? 'border-emerald-500 bg-emerald-50' : 'border-amber-500 bg-amber-50'}`}
              data-testid="hpd-verify-result"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className={`font-semibold text-sm ${hpdVerifyResult.success ? 'text-emerald-900' : 'text-amber-900'}`}>
                    {hpdVerifyResult.success
                      ? `✅ Verification PASSED — zero traces left for ${hpdVerifyResult.date_from} → ${hpdVerifyResult.date_to}`
                      : `⚠️ Verification FOUND ${hpdVerifyResult.remaining_total.toLocaleString()} rows still present`}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Verified at {new Date(hpdVerifyResult.verified_at).toLocaleString()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setHpdVerifyResult(null)}
                  className="text-xs"
                  data-testid="hpd-verify-dismiss-btn"
                >
                  Dismiss
                </Button>
              </div>
              {!hpdVerifyResult.success && hpdVerifyResult.remaining_by_collection.length > 0 && (
                <div className="mt-2 bg-white/60 border border-amber-200 rounded p-2 max-h-40 overflow-y-auto">
                  <div className="font-semibold text-[11px] text-amber-900 mb-1">Surviving rows by collection:</div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-mono">
                    {hpdVerifyResult.remaining_by_collection.map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-amber-100/50 py-0.5">
                        <span className="text-slate-700">{k.replace(/_/g, ' ')}</span>
                        <span className="font-bold text-amber-700">{v.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1">
                    This is unusual — please contact support if it persists after a retry.
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone - Erase All Data */}
      <Card className="border-red-300 dark:border-red-800">
        <CardHeader className="bg-red-50 dark:bg-red-950/30 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Danger Zone
          </CardTitle>
          <CardDescription className="text-red-600/80">
            Irreversible actions - Use with extreme caution
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>
              Erasing all data will permanently delete everything including all backups stored in the database.
              This action cannot be undone. Make sure to download an external backup first!
            </AlertDescription>
          </Alert>
          
          <div className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50/50 dark:bg-red-950/20 space-y-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="font-medium text-red-700 dark:text-red-400">Erase All Data (true wipe)</p>
                <p className="text-sm text-red-600/70 dark:text-red-400/70">
                  Wipes every transactional / audit / backup collection — including orphan rows with no date.
                  Master data preserved: ledger names · products · shipping prices · aliases · users.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleEraseAllData}
                disabled={eraseAllPreviewLoading}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="erase-all-preview-btn"
              >
                {eraseAllPreviewLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                ) : (
                  <><Eye className="w-4 h-4 mr-2" /> Preview impact</>
                )}
              </Button>
            </div>

            {eraseAllPreview && (
              <div className="space-y-3 p-3 border-2 border-red-500 rounded-md bg-red-100/40 dark:bg-red-950/40">
                <div className="text-sm font-medium text-red-700">
                  TOTAL TO WIPE: <span className="font-bold">{eraseAllPreview.total_to_wipe.toLocaleString()}</span> rows ·{" "}
                  <span className="font-bold">{eraseAllPreview.wipe_collection_count}</span> collection(s)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="bg-white/70 border border-red-200 rounded p-2 max-h-48 overflow-y-auto">
                    <div className="font-semibold text-[11px] text-red-900 mb-1">🧹 WILL WIPE (non-master):</div>
                    <div className="text-[10px] font-mono space-y-0.5">
                      {Object.entries(eraseAllPreview.to_wipe).filter(([, v]) => v > 0).map(([k, v]) => (
                        <div key={k} className="flex justify-between border-b border-red-100/50 py-0.5">
                          <span className="text-slate-700">{k.replace(/_/g, " ")}</span>
                          <span className="font-bold text-red-700">{v.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white/70 border border-emerald-200 rounded p-2 max-h-48 overflow-y-auto">
                    <div className="font-semibold text-[11px] text-emerald-900 mb-1">✅ WILL PRESERVE (master):</div>
                    <div className="text-[10px] font-mono space-y-0.5">
                      {Object.entries(eraseAllPreview.to_keep).filter(([, v]) => v > 0).map(([k, v]) => (
                        <div key={k} className="flex justify-between border-b border-emerald-100/50 py-0.5">
                          <span className="text-slate-700">{k.replace(/_/g, " ")}</span>
                          <span className="font-bold text-emerald-700">{v.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-red-700">1. Download a fresh backup:</label>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try { await handleDownloadBackup(); setEraseAllBackupDone(true); toast.success("Backup saved."); }
                      catch (e) { /* error already shown */ }
                    }}
                    disabled={eraseAllBackupDone}
                    className={`mt-1 w-full ${eraseAllBackupDone ? "border-emerald-400 text-emerald-700 bg-emerald-50" : "border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100"}`}
                    data-testid="erase-all-backup-btn"
                  >
                    {eraseAllBackupDone ? "✅ Backup downloaded — proceed" : "🛟 Backup before wipe"}
                  </Button>
                </div>
                <div>
                  <label className="text-sm font-medium text-red-700">2. Type <code className="bg-red-200 px-1 rounded">ERASE ALL DATA</code> exactly:</label>
                  <input
                    type="text"
                    value={eraseAllConfirm}
                    onChange={(e) => setEraseAllConfirm(e.target.value)}
                    placeholder="ERASE ALL DATA"
                    className="mt-1 w-full px-3 py-2 text-sm font-mono border border-red-300 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="erase-all-confirm-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-red-700">3. Admin password:</label>
                  <input
                    type="password"
                    value={eraseAllPassword}
                    onChange={(e) => setEraseAllPassword(e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm border border-red-300 rounded-md bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="erase-all-password-input"
                  />
                </div>
                <Button
                  variant="destructive"
                  onClick={handleEraseAllExecute}
                  disabled={erasing || eraseAllConfirm !== "ERASE ALL DATA" || !eraseAllPassword || !eraseAllBackupDone}
                  className="w-full bg-red-700 hover:bg-red-800 text-white"
                  data-testid="erase-all-execute-btn"
                >
                  {erasing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Erasing…</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" /> ERASE EVERYTHING NOW</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Anonymous Sales drill-down editor */}
      <Dialog open={anonymousOpen} onOpenChange={setAnonymousOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-rose-600" />
              Truly Anonymous Sales — manual name entry
            </DialogTitle>
            <DialogDescription>
              These parcels have no customer name on either <code>sales</code> or{" "}
              <code>shipment_orders</code>. Type a name and save — it will be written to every
              matching row across both collections.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 pb-2">
            <Input
              placeholder="Filter by tracking or client…"
              value={anonymousSearch}
              onChange={(e) => setAnonymousSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchAnonymousSales(1, anonymousSearch); }}
              className="h-9"
              data-testid="anon-search-input"
            />
            <Button size="sm" variant="outline" onClick={() => fetchAnonymousSales(1, anonymousSearch)} disabled={anonymousLoading} data-testid="anon-search-btn">
              {anonymousLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Search"}
            </Button>
            <Badge variant="outline" className="text-[11px]">
              ~{anonymousTotal.toLocaleString()} total
            </Badge>
          </div>

          <div className="flex-1 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                <tr>
                  <th className="text-left p-2">Tracking</th>
                  <th className="text-left p-2">Client</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Product</th>
                  <th className="text-left p-2 w-[260px]">Customer name</th>
                  <th className="text-right p-2 w-[100px]">Save</th>
                </tr>
              </thead>
              <tbody>
                {anonymousLoading ? (
                  <tr><td colSpan={6} className="text-center text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                ) : anonymousItems.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-slate-500 p-6 italic">No truly-anonymous rows on this page.</td></tr>
                ) : anonymousItems.map((row) => (
                  <tr key={row.tracking} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`anon-row-${row.tracking}`}>
                    <td className="p-2 font-mono text-xs">{row.tracking}</td>
                    <td className="p-2">{row.client_code || "-"}</td>
                    <td className="p-2 text-xs">{row.sale_date || row.order_date || "-"}</td>
                    <td className="p-2 truncate max-w-[200px]" title={row.product_name || row.second_name}>{row.product_name || row.second_name || "-"}</td>
                    <td className="p-2">
                      <Input
                        value={anonymousDraft[row.tracking] || ""}
                        onChange={(e) => setAnonymousDraft((prev) => ({ ...prev, [row.tracking]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSaveAnonymousName(row.tracking); }}
                        placeholder="Type customer name…"
                        className="h-8 text-sm"
                        data-testid={`anon-input-${row.tracking}`}
                      />
                    </td>
                    <td className="p-2 text-right">
                      <Button
                        size="sm"
                        onClick={() => handleSaveAnonymousName(row.tracking)}
                        disabled={anonymousSavingTrk === row.tracking || (anonymousDraft[row.tracking] || "").trim().length < 2}
                        className="h-8 bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid={`anon-save-${row.tracking}`}
                      >
                        {anonymousSavingTrk === row.tracking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between gap-2 border-t border-slate-200 dark:border-slate-700 pt-3">
            <div className="text-xs text-slate-500">
              Page {anonymousPage} · enter ↩︎ saves the current row
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => fetchAnonymousSales(Math.max(anonymousPage - 1, 1), anonymousSearch)} disabled={anonymousLoading || anonymousPage <= 1} data-testid="anon-prev">
                ← Prev
              </Button>
              <Button size="sm" variant="outline" onClick={() => fetchAnonymousSales(anonymousPage + 1, anonymousSearch)} disabled={anonymousLoading || anonymousItems.length < 25} data-testid="anon-next">
                Next →
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAnonymousOpen(false)} data-testid="anon-close">
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Garbage code hard purge confirmation */}
      <Dialog open={garbagePurgeOpen} onOpenChange={setGarbagePurgeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Hard-purge code "{garbagePurgeCode}"
            </DialogTitle>
            <DialogDescription>
              Deletes every <code>shipment_orders / sales / ledger_entries / payment_followups</code> row
              with this client_code. No snapshot is taken. Irreversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Type "HARD PURGE" exactly</label>
              <Input
                value={garbagePurgePhrase}
                onChange={(e) => setGarbagePurgePhrase(e.target.value)}
                placeholder="HARD PURGE"
                className="font-mono"
                data-testid="garbage-purge-phrase"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Admin password</label>
              <Input
                type="password"
                value={garbagePurgePassword}
                onChange={(e) => setGarbagePurgePassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleGarbagePurge(); }}
                data-testid="garbage-purge-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGarbagePurgeOpen(false)} data-testid="garbage-purge-cancel">Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleGarbagePurge}
              disabled={!garbagePurgePassword || garbagePurgePhrase !== "HARD PURGE" || garbageBusyCode === garbagePurgeCode}
              data-testid="garbage-purge-confirm"
            >
              {garbageBusyCode === garbagePurgeCode ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Purge permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Garbage code bulk hard purge (pure-narration) */}
      <Dialog open={bulkPurgeOpen} onOpenChange={setBulkPurgeOpen}>
        <DialogContent className="max-w-lg" data-testid="garbage-bulk-purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Purge Pure-Narration Codes
            </DialogTitle>
            <DialogDescription>
              Deletes every <code>shipment_orders / sales / ledger_entries / payment_followups</code> row
              for garbage codes that have <strong>no fuzzy ledger match</strong> AND ≥2 suspicious reasons.
              No snapshot. Irreversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {bulkPurgePreviewLoading ? (
              <div className="text-sm text-slate-500 italic flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Scanning…
              </div>
            ) : bulkPurgePreview && bulkPurgePreview.codes && bulkPurgePreview.codes.length > 0 ? (
              <>
                <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 rounded-md px-3 py-2 text-xs">
                  <div className="font-semibold text-rose-800">
                    {bulkPurgePreview.total_codes} code(s) · {bulkPurgePreview.total_rows_would_delete.toLocaleString()} row(s) will be permanently deleted
                  </div>
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                    {bulkPurgePreview.codes.map(c => (
                      <div key={c} className="flex justify-between gap-2 font-mono text-[11px]">
                        <span className="truncate">{c}</span>
                        <span className="text-rose-700">{(bulkPurgePreview.per_code_preview?.[c] || 0).toLocaleString()} rows</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Type "HARD PURGE" exactly</label>
                  <Input
                    value={bulkPurgePhrase}
                    onChange={(e) => setBulkPurgePhrase(e.target.value)}
                    placeholder="HARD PURGE"
                    className="font-mono"
                    data-testid="bulk-purge-phrase"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Admin password</label>
                  <Input
                    type="password"
                    value={bulkPurgePassword}
                    onChange={(e) => setBulkPurgePassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && bulkPurgePassword && bulkPurgePhrase === "HARD PURGE") handleBulkPurge(); }}
                    data-testid="bulk-purge-password"
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-emerald-700 italic">No pure-narration codes to purge. Every garbage code either has a fuzzy match (use Remap) or fewer than 2 suspicious reasons (handle manually).</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkPurgeOpen(false)} data-testid="bulk-purge-cancel">Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleBulkPurge}
              disabled={
                !bulkPurgePreview || !bulkPurgePreview.codes || bulkPurgePreview.codes.length === 0 ||
                !bulkPurgePassword || bulkPurgePhrase !== "HARD PURGE" || bulkPurgeRunning
              }
              data-testid="bulk-purge-confirm"
            >
              {bulkPurgeRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Purge all permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagementPage;
